import React, { useEffect, useMemo, useRef, useState } from "react";
import "./AudioRecorder.scss";
import axios from "axios";
import Lottie from "lottie-react";
import uploadingAnimation from "../../../components/animation/Ai loading model.json";
import completeAnimation from "../../../components/animation/complete.json";
import modelAnimation from "../../../components/animation/Keyboard Typing.json";
import keyboardTypingAnimation from "../../../components/animation/Keyboard Typing.json";

/* --------------------------- Load CKEditor Script --------------------------- */
const loadCKEditorScript = () =>
  new Promise<void>((resolve, reject) => {
    if ((window as any).CKEDITOR) {
      resolve();
      return;
    }

    const existingScript = document.querySelector(
      'script[data-ckeditor-script="true"]',
    );

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(), { once: true });
      existingScript.addEventListener(
        "error",
        () => reject(new Error("Failed to load CKEditor.")),
        { once: true },
      );
      return;
    }

    const script = document.createElement("script");
    script.src = "/ckeditor/ckeditor.js";
    script.async = true;
    script.dataset.ckeditorScript = "true";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load CKEditor."));
    document.body.appendChild(script);
  });

type UploadStatus =
  | "idle"
  | "queued"
  | "uploading"
  | "uploaded"
  | "failed"
  | "expired";

type SavedRecording = {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  durationSeconds: number;
  durationText: string;
  createdAt: string;
  objectUrl: string;
  uploadStatus: UploadStatus;
  uploadProgress?: number;
  uploadSessionId?: string;
  remoteKey?: string;
  remoteLocation?: string;
  errorMessage?: string;
};

type PersistedRecordingMeta = Omit<SavedRecording, "objectUrl">;

type AudioInputDevice = {
  deviceId: string;
  label: string;
  groupId?: string;
};

type StoredMicPreference = {
  deviceId: string;
  label: string;
  savedAt: string;
};

type ConfirmAction =
  | "pause"
  | "resume"
  | "stop"
  | "startTranscribe"
  | "replaceExistingAudio"
  | "delete"
  | "clearAll"
  | "unsafeClose";

type ConfirmModalState = {
  action: ConfirmAction;
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel?: string;
  danger?: boolean;
  variant?: "default" | "danger" | "process";
  details?: string[];
  recordingId?: string;
};

type PlayerState = {
  currentTime: number;
  duration: number;
};

type PresignedUrlItem =
  | string
  | {
      url?: string;
      presigned_url?: string;
      upload_url?: string;
      part_number?: number;
      partNumber?: number;
    };

type GenerateUploadResponse = {
  upload_session_id: string;
  part_size: number;
  urls: PresignedUrlItem[];
};

type CompleteUploadResponse = {
  upload_session_id?: string;
  status?: string;
  etag?: string;
  ETag?: string;
  location?: string;
  Location?: string;
  key?: string;
  Key?: string;
  [key: string]: unknown;
};

type GeneratePatientNoteResponse = {
  upload_session_id?: string;
  audio_id?: string | number;
  status: string;
  patient_note?: unknown;
  patient_note_body?: unknown;
  [key: string]: unknown;
};

type AudioRecordDataResponse = {
  [key: string]: unknown;
};

type ProcessingState =
  | "unknown"
  | "pending"
  | "processing"
  | "completed"
  | "failed";

type AudioProcessingSnapshot = {
  transcriptState: ProcessingState;
  patientNoteState: ProcessingState;
  transcriptStatus: unknown;
  patientNoteStatus: unknown;
  patientNote: unknown;
};

type TreatmentPlanAudioCheckResponse = {
  detail?: string;
  error?: string;
  result?: boolean;
  exists?: boolean;
  has_recording?: boolean;
  has_audio_recording?: boolean;
  [key: string]: unknown;
};

type PatientNoteReadyPayload = {
  uploadSessionId: string;
  status: string;
  patientNote: unknown;
  patientNoteText: string;
  treatmentPlanId: string | number;
  consentBundleId: string | number;
  response: GeneratePatientNoteResponse;
};

type PatientNoteEditorState = {
  isOpen: boolean;
  uploadSessionId: string;
  audioId: string;
  patientNoteText: string;
  patientNoteHtml: string;
  originalPatientNote: unknown;
  status: string;
  isSaving: boolean;
  errorMessage?: string;
};

type UploadPartResult = {
  part_number: number;
  etag: string;
  size: number;
};

type DebugS3UploadState = {
  status: "idle" | "requesting" | "uploading" | "partsUploaded" | "failed";
  fileName: string;
  progress: number;
  uploadSessionId: string;
  uploadedParts: number;
  totalParts: number;
  errorMessage: string;
};

type PendingUploadJob = {
  recordingId: string;
  consentBundleId: string | number;
  treatmentPlanId: string | number;
  uploadSessionId: string;
  fileName: string;
  mimeType: string;
  size: number;
  durationSeconds: number;
  recordedAt: string;
  partSize: number;
  urls: PresignedUrlItem[];
  attempts: number;
  maxAttempts: number;
  createdAtMs: number;
  expiresAtMs: number;
  lastAttemptAtMs?: number;
  uploadedParts: UploadPartResult[];
  status:
    | "queued"
    | "uploading"
    | "finalizing"
    | "failed"
    | "completed"
    | "expired";
  lastError?: string;
};

type AudioWorkflowStage =
  | "idle"
  | "generatingLink"
  | "uploading"
  | "uploadComplete"
  | "transcribing"
  | "transcriptionComplete"
  | "generatingPatientNote"
  | "patientNoteComplete"
  | "failed";

type AudioWorkflowOverlayState = {
  isOpen: boolean;
  stage: AudioWorkflowStage;
  progress?: number;
  currentStatus?: string;
  errorMessage?: string;
  failedRecordingId?: string;
  retryAction?: "transcription";
  failedUploadSessionId?: string;
};

export type PersistedAudioWorkflow = {
  treatmentPlanId: string;
  consentBundleId: string | number;
  uploadSessionId: string;
  recordingId: string;
  stage: AudioWorkflowStage;
  progress?: number;
  currentStatus?: string;
  retryAction?: "transcription";
  updatedAtMs: number;
};

export type AudioUnsafeCloseState = {
  isUnsafe: boolean;
  reason: "recording" | "uploading" | "processing" | null;
  title: string;
  message: string;
  isRecording: boolean;
  isUploadInProgress: boolean;
  isWorkflowInProgress: boolean;
};

type AudioRecorderProps = {
  consentBundleId: string | number;
  treatmentPlanId: string | number;
  initialAudioRecordData?: AudioRecordDataResponse | null;
  apiBaseUrl?: string;
  uploadEnabled?: boolean;
  uploadDisabledMessage?: string;
  recordDisabled?: boolean;
  recordDisabledLabel?: string;
  recordDisabledMessage?: string;
  skipTreatmentPlanCheckOnRecord?: boolean;
  matchConsentBundleId?: boolean;
  onCreateConsentBundleIfMissing?: (treatmentPlanId: string) => Promise<void>;
  onTreatmentPlanCheckPassed?: (treatmentPlanId: string) => void;
  maxRecordings?: number;
  retryWindowMinutes?: number;
  maxUploadAttempts?: number;
  retryPollMs?: number;
  authToken?: string;
  onUploadComplete?: (payload: {
    recording: SavedRecording;
    response: CompleteUploadResponse;
  }) => void;
  onUploadFailed?: (payload: {
    recording: SavedRecording;
    error: unknown;
  }) => void;
  onRequestClose?: () => void;
  onUnsafeStateChange?: (state: AudioUnsafeCloseState) => void;
  onPatientNoteReady?: (payload: PatientNoteReadyPayload) => void;
  onPatientNoteSaved?: (payload: {
    uploadSessionId: string;
    editedPatientNote: unknown;
    response: unknown;
    audioRecordData: AudioRecordDataResponse | null;
  }) => void;
};

const DB_NAME = "audio_recorder_db";
const DB_VERSION = 1;
const STORE_NAME = "recordings";
const META_STORAGE_KEY = "local_audio_recording_meta_v3";
const PENDING_UPLOADS_KEY = "local_audio_recording_pending_uploads_v1";
const ACTIVE_AUDIO_WORKFLOWS_KEY = "audio_recorder_active_workflows_v1";
export const ACTIVE_AUDIO_WORKFLOW_EVENT = "audio-recorder-workflow-change";
const MIC_PREFERENCE_STORAGE_KEY = "audio_recorder_selected_mic_v1";
const DEFAULT_MAX_RECORDINGS = 1;
const DEFAULT_RETRY_WINDOW_MINUTES = 20;
const DEFAULT_MAX_UPLOAD_ATTEMPTS = 5;
const DEFAULT_RETRY_POLL_MS = 30_000;

const GENERATE_TRANSCRIPT_AND_PATIENT_NOTE_PATH = "/audio-recording/generate_transcript_and_patient_note";
const GET_AUDIO_RECORD_DATA_PATH = "/audio-recording/get_audio_record_data";
const EDIT_PATIENT_NOTE_PATH = "/audio-recording/edit_patient_note";
const AUDIO_WORKFLOW_SUCCESS_PAUSE_MS = 900;
const AUDIO_WORKFLOW_POLL_MS = 5_000;
const AUDIO_WORKFLOW_MAX_POLLS = 360;
const inFlightUploadSessions = new Set<string>();

const AUDIO_WORKFLOW_STAGE_META: Record<
  AudioWorkflowStage,
  {
    eyebrow: string;
    title: string;
    description: string;
    animationData: any;
    isComplete?: boolean;
    isError?: boolean;
  }
> = {
  idle: {
    eyebrow: "Waiting",
    title: "Ready",
    description: "The audio workflow has not started yet.",
    animationData: uploadingAnimation,
  },
  generatingLink: {
    eyebrow: "Generating link",
    title: "Generating cloud link",
    description: "Preparing secure upload.",
    animationData: uploadingAnimation,
  },
  uploading: {
    eyebrow: "Uploading",
    title: "Uploading to cloud",
    description: "Uploading audio to cloud.",
    animationData: uploadingAnimation,
  },
  uploadComplete: {
    eyebrow: "Upload completed",
    title: "Upload completed",
    description: "Starting transcription.",
    animationData: completeAnimation,
    isComplete: true,
  },
  transcribing: {
    eyebrow: "Transcribing",
    title: "Transcribing audio",
    description: "Generating transcript.",
    animationData: modelAnimation,
  },
  transcriptionComplete: {
    eyebrow: "Transcript completed",
    title: "Transcription completed",
    description: "Generating patient note.",
    animationData: completeAnimation,
    isComplete: true,
  },
  generatingPatientNote: {
    eyebrow: "Generating note",
    title: "Generating patient note",
    description: "Creating patient note.",
    animationData: keyboardTypingAnimation,
  },
  patientNoteComplete: {
    eyebrow: "Completed",
    title: "Completed",
    description: "Audio processing completed.",
    animationData: completeAnimation,
    isComplete: true,
  },
  failed: {
    eyebrow: "Failed",
    title: "Workflow failed",
    description: "Something went wrong while processing the audio.",
    animationData: modelAnimation,
    isError: true,
  },
};

const AUDIO_WORKFLOW_SIMPLE_STATUS: Record<AudioWorkflowStage, string> = {
  idle: "Waiting",
  generatingLink: "Generating cloud link",
  uploading: "Uploading to cloud",
  uploadComplete: "Upload completed",
  transcribing: "Transcribing audio",
  transcriptionComplete: "Transcription completed",
  generatingPatientNote: "Generating patient note",
  patientNoteComplete: "Completed",
  failed: "Failed",
};

function getWorkflowVisualProgress(stage: AudioWorkflowStage, progress = 0) {
  const safeProgress = Math.max(0, Math.min(100, Math.round(progress || 0)));

  if (stage === "uploading") return safeProgress;
  if (stage === "generatingLink") return 18;
  if (stage === "uploadComplete") return 45;
  if (stage === "transcribing") return 62;
  if (stage === "transcriptionComplete") return 75;
  if (stage === "generatingPatientNote") return 88;
  if (stage === "patientNoteComplete") return 100;
  if (stage === "failed") return safeProgress || 100;

  return 0;
}

function isWorkflowIndeterminateStage(stage: AudioWorkflowStage) {
  return ["generatingLink", "transcribing", "generatingPatientNote"].includes(
    stage,
  );
}

function wait(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function normalizeWorkflowStatus(value: unknown) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function isWorkflowFailedStatus(value: unknown) {
  return ["failed", "error", "cancelled", "canceled"].includes(
    normalizeWorkflowStatus(value),
  );
}

function getNestedValue(source: unknown, path: string) {
  if (!source || typeof source !== "object") return undefined;

  return path.split(".").reduce<unknown>((value, key) => {
    if (!value || typeof value !== "object") return undefined;
    return (value as Record<string, unknown>)[key];
  }, source);
}

function getAudioRecordRoots(response: AudioRecordDataResponse) {
  const candidates = [
    getNestedValue(response, "data.audio_record"),
    getNestedValue(response, "data.audio_recording"),
    getNestedValue(response, "result.audio_record"),
    getNestedValue(response, "result.audio_recording"),
    getNestedValue(response, "audio_record"),
    getNestedValue(response, "audio_recording"),
    getNestedValue(response, "data"),
    getNestedValue(response, "result"),
    response,
  ];

  return candidates.filter((candidate): candidate is Record<string, unknown> =>
    Boolean(candidate && typeof candidate === "object"),
  );
}

function getFirstAudioRecordValue(
  response: AudioRecordDataResponse,
  paths: string[],
) {
  for (const root of getAudioRecordRoots(response)) {
    for (const path of paths) {
      const value = getNestedValue(root, path);
      if (value !== undefined && value !== null && value !== "") return value;
    }
  }

  return undefined;
}

function getUploadSessionIdFromAudioRecordData(
  response: AudioRecordDataResponse,
) {
  return String(
    getFirstAudioRecordValue(response, [
      "upload_session_id",
      "uploadSessionId",
      "audio.upload_session_id",
      "audio_record.upload_session_id",
      "audio_recording.upload_session_id",
      "upload_session.id",
    ]) ?? "",
  ).trim();
}

function normalizeProcessingState(value: unknown): ProcessingState {
  if (value === true) return "completed";
  if (value === false || value === null || value === undefined)
    return "unknown";

  const normalized = normalizeWorkflowStatus(value).replace(/[\s-]+/g, "_");

  if (!normalized) return "unknown";
  if (
    normalized.startsWith("not_") ||
    normalized.includes("not_generated") ||
    normalized.includes("not_started")
  ) {
    return "pending";
  }
  if (
    ["failed", "failure", "error", "errored", "cancelled", "canceled"].some(
      (status) => normalized === status || normalized.includes(status),
    )
  ) {
    return "failed";
  }
  if (
    [
      "completed",
      "complete",
      "success",
      "succeeded",
      "generated",
      "done",
      "ready",
      "saved",
    ].some((status) => normalized === status || normalized.includes(status))
  ) {
    return "completed";
  }
  if (
    [
      "processing",
      "in_progress",
      "transcribing",
      "generating",
      "started",
      "initiated",
      "running",
    ].some((status) => normalized === status || normalized.includes(status))
  ) {
    return "processing";
  }
  if (["pending", "queued", "waiting", "idle"].includes(normalized)) {
    return "pending";
  }

  return "unknown";
}

function getAudioProcessingSnapshot(
  response: AudioRecordDataResponse,
): AudioProcessingSnapshot {
  const primaryRecord = getAudioRecordRoots(response)[0] || response;
  const overallStatus =
    getNestedValue(primaryRecord, "processing_status") ??
    getNestedValue(primaryRecord, "workflow_status") ??
    getNestedValue(primaryRecord, "status");
  const transcriptStatus = getFirstAudioRecordValue(response, [
    "transcription_status",
    "transcript_status",
    "transcription.status",
    "transcript.status",
  ]);
  const patientNoteStatus = getFirstAudioRecordValue(response, [
    "patient_note_status",
    "patient_note_generation_status",
    "patientNoteStatus",
    "patient_note.status",
    "patientNote.status",
  ]);
  const patientNote = getFirstAudioRecordValue(response, [
    "patient_note_body",
    "patient_note.content",
    "patient_note.text",
    "patient_note.note",
    "patientNote.content",
    "patientNote.text",
    "patientNote.note",
    "generated_patient_note",
    "patient_note",
    "patientNote",
  ]);

  let transcriptState = normalizeProcessingState(transcriptStatus);
  let patientNoteState = normalizeProcessingState(patientNoteStatus);
  const normalizedOverallStatus = normalizeWorkflowStatus(
    overallStatus,
  ).replace(/[\s-]+/g, "_");

  if (transcriptState === "unknown") {
    if (normalizedOverallStatus.includes("transcript")) {
      transcriptState = normalizeProcessingState(normalizedOverallStatus);
    } else if (normalizedOverallStatus.includes("patient")) {
      transcriptState = "completed";
    } else if (normalizeProcessingState(overallStatus) === "completed") {
      transcriptState = "completed";
    } else if (normalizeProcessingState(overallStatus) === "processing") {
      transcriptState = "processing";
    } else if (normalizeProcessingState(overallStatus) === "pending") {
      transcriptState = "pending";
    } else if (normalizeProcessingState(overallStatus) === "failed") {
      transcriptState = "failed";
    }
  }

  if (patientNoteState === "unknown") {
    if (normalizedOverallStatus.includes("patient")) {
      patientNoteState = normalizeProcessingState(normalizedOverallStatus);
    } else if (
      normalizeProcessingState(overallStatus) === "completed" ||
      patientNote !== undefined
    ) {
      patientNoteState = "completed";
    } else if (transcriptState === "completed") {
      patientNoteState = "processing";
    }
  }

  return {
    transcriptState,
    patientNoteState,
    transcriptStatus: transcriptStatus ?? overallStatus,
    patientNoteStatus: patientNoteStatus ?? overallStatus,
    patientNote,
  };
}

function doesAudioRecordMatchContext(
  response: AudioRecordDataResponse,
  treatmentPlanId: string | number,
  consentBundleId?: string | number,
  matchConsentBundleId = true,
) {
  const expectedTreatmentPlanId = String(treatmentPlanId ?? "").trim();
  const responseTreatmentPlanId = String(
    getFirstAudioRecordValue(response, [
      "treatment_plan_id",
      "treatmentPlanId",
    ]) ?? "",
  ).trim();

  if (
    !expectedTreatmentPlanId ||
    !responseTreatmentPlanId ||
    responseTreatmentPlanId !== expectedTreatmentPlanId
  ) {
    return false;
  }

  const expectedConsentBundleId = String(consentBundleId ?? "").trim();
  if (!matchConsentBundleId || !expectedConsentBundleId) return true;

  const responseConsentBundleId = String(
    getFirstAudioRecordValue(response, [
      "consent_bundle_id",
      "consentBundleId",
    ]) ?? "",
  ).trim();

  return (
    Boolean(responseConsentBundleId) &&
    responseConsentBundleId === expectedConsentBundleId
  );
}

export function hasGeneratedPatientNoteInAudioRecordData(
  response: unknown,
  treatmentPlanId?: string | number,
  consentBundleId?: string | number,
  matchConsentBundleId = true,
) {
  if (!response || typeof response !== "object") return false;

  if (
    treatmentPlanId !== undefined &&
    !doesAudioRecordMatchContext(
      response as AudioRecordDataResponse,
      treatmentPlanId,
      consentBundleId,
      matchConsentBundleId,
    )
  ) {
    return false;
  }

  const patientNoteStatus = normalizeWorkflowStatus(
    getFirstAudioRecordValue(response as AudioRecordDataResponse, [
      "patient_note_status",
      "patient_note_generation_status",
      "patientNoteStatus",
      "patient_note.status",
      "patientNote.status",
    ]),
  ).replace(/[\s-]+/g, "_");

  if (patientNoteStatus !== "generated") return false;

  const snapshot = getAudioProcessingSnapshot(
    response as AudioRecordDataResponse,
  );

  return (
    snapshot.patientNoteState === "completed" &&
    formatPatientNoteForEditor(snapshot.patientNote).trim().length > 0
  );
}

function formatTime(totalSeconds: number) {
  const safeSeconds = Number.isFinite(totalSeconds)
    ? Math.max(0, Math.floor(totalSeconds))
    : 0;

  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;

  if (hours > 0) {
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0",
    )}:${String(seconds).padStart(2, "0")}`;
  }

  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
    2,
    "0",
  )}`;
}

function getBestMimeType() {
  const types = [
    "audio/ogg;codecs=opus",
    "audio/ogg",
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "",
  ];

  for (const type of types) {
    if (!type) return "";
    if (MediaRecorder.isTypeSupported(type)) return type;
  }

  return "";
}

function getFileExtension(mimeType: string, fileName = "") {
  const nameExtension = fileName.split(".").pop()?.toLowerCase();

  if (nameExtension && nameExtension.length <= 5) return nameExtension;
  if (mimeType.includes("ogg")) return "ogg";
  if (mimeType.includes("mp4")) return "m4a";
  if (mimeType.includes("mpeg")) return "mp3";
  if (mimeType.includes("wav")) return "wav";

  return "webm";
}

function createId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function getCookieValue(name: string) {
  if (typeof document === "undefined") return "";

  const value = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`))
    ?.split("=")[1];

  return value ? decodeURIComponent(value) : "";
}

function getDefaultAuthToken() {
  if (typeof window === "undefined") return "";

  return (
    localStorage.getItem("token") ||
    localStorage.getItem("access_token") ||
    localStorage.getItem("auth_token") ||
    getCookieValue("auth_token") ||
    ""
  );
}

function getStoredMicPreference(): StoredMicPreference | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = localStorage.getItem(MIC_PREFERENCE_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);

    if (
      parsed &&
      typeof parsed.deviceId === "string" &&
      typeof parsed.label === "string"
    ) {
      return parsed;
    }
  } catch (error) {
    console.error("Failed to read saved microphone preference:", error);
  }

  return null;
}

function saveMicPreference(mic: AudioInputDevice) {
  if (typeof window === "undefined") return;

  try {
    const payload: StoredMicPreference = {
      deviceId: mic.deviceId,
      label: mic.label,
      savedAt: new Date().toISOString(),
    };

    localStorage.setItem(MIC_PREFERENCE_STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    console.error("Failed to save microphone preference:", error);
  }
}

function normalizeMicLabel(label: string) {
  return label
    .toLowerCase()
    .replace(/\b(default|communications?)\b/g, "")
    .replace(/[()]/g, " ")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function isMicAlias(mic: AudioInputDevice) {
  const value = `${mic.deviceId} ${mic.label}`.toLowerCase();
  return value.includes("default") || value.includes("communications");
}

function getMicPreferenceScore(mic: AudioInputDevice) {
  const label = mic.label.toLowerCase();
  let score = 50;

  if (label.includes("usb")) score += 35;
  if (label.includes("external")) score += 24;
  if (label.includes("mic") || label.includes("microphone")) score += 12;
  if (label.includes("headset") || label.includes("headphone")) score += 10;
  if (label.includes("array")) score += 6;
  if (label.includes("default")) score -= 12;
  if (label.includes("communications")) score -= 14;
  if (label.includes("virtual")) score -= 35;
  if (label.includes("stereo mix") || label.includes("what u hear"))
    score -= 45;

  return Math.max(0, Math.min(100, score));
}

function getBestAudioInputDevice(microphones: AudioInputDevice[]) {
  return [...microphones].sort((a, b) => {
    const scoreDiff = getMicPreferenceScore(b) - getMicPreferenceScore(a);
    if (scoreDiff !== 0) return scoreDiff;
    return a.label.localeCompare(b.label);
  })[0];
}

function dedupeAudioInputDevices(microphones: AudioInputDevice[]) {
  const byKey = new Map<string, AudioInputDevice>();

  microphones.forEach((mic) => {
    const normalizedLabel = normalizeMicLabel(mic.label);
    const key = mic.groupId
      ? `group:${mic.groupId}:${normalizedLabel || "unknown"}`
      : normalizedLabel || `device:${mic.deviceId}`;
    const existing = byKey.get(key);

    if (!existing) {
      byKey.set(key, mic);
      return;
    }

    const existingScore =
      getMicPreferenceScore(existing) - (isMicAlias(existing) ? 30 : 0);
    const nextScore = getMicPreferenceScore(mic) - (isMicAlias(mic) ? 30 : 0);

    if (nextScore > existingScore) {
      byKey.set(key, mic);
    }
  });

  return Array.from(byKey.values()).sort((a, b) => {
    const aliasDiff = Number(isMicAlias(a)) - Number(isMicAlias(b));
    if (aliasDiff !== 0) return aliasDiff;
    return a.label.localeCompare(b.label);
  });
}

function getMicInputHint(mic: AudioInputDevice, selectedMicId: string) {
  if (selectedMicId === mic.deviceId) return "Selected input";

  const score = getMicPreferenceScore(mic);
  if (score >= 78) return "Recommended input";
  if (score >= 58) return "Available input";
  return "Backup input";
}

const token = axios.defaults.headers.common.Authorization;

function buildApiUrl(apiBaseUrl: string, path: string) {
  const cleanBase = apiBaseUrl.replace(/\/$/, "");
  return cleanBase ? `${cleanBase}${path}` : path;
}

function getUploadUrl(item: PresignedUrlItem) {
  if (typeof item === "string") return item;
  return item.url || item.presigned_url || item.upload_url || "";
}

function getPartNumber(item: PresignedUrlItem, index: number) {
  if (typeof item === "string") return index + 1;
  return item.part_number || item.partNumber || index + 1;
}

function getReadableFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getStoredRecordingMeta(): PersistedRecordingMeta[] {
  try {
    const raw = localStorage.getItem(META_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error("Failed to read recording metadata:", error);
    return [];
  }
}

function saveRecordingMeta(recordings: SavedRecording[]) {
  const meta: PersistedRecordingMeta[] = recordings.map(
    ({ objectUrl, ...recording }) => recording,
  );

  localStorage.setItem(META_STORAGE_KEY, JSON.stringify(meta));
}

function getPendingUploadJobs(): PendingUploadJob[] {
  try {
    const raw = localStorage.getItem(PENDING_UPLOADS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error("Failed to read pending uploads:", error);
    return [];
  }
}

function savePendingUploadJobs(jobs: PendingUploadJob[]) {
  localStorage.setItem(PENDING_UPLOADS_KEY, JSON.stringify(jobs));
}

function upsertPendingUploadJob(job: PendingUploadJob) {
  const jobs = getPendingUploadJobs();
  const existingIndex = jobs.findIndex(
    (item) => item.uploadSessionId === job.uploadSessionId,
  );

  if (existingIndex >= 0) {
    jobs[existingIndex] = job;
  } else {
    jobs.unshift(job);
  }

  savePendingUploadJobs(jobs);
}

function removePendingUploadJob(uploadSessionId: string) {
  savePendingUploadJobs(
    getPendingUploadJobs().filter(
      (job) => job.uploadSessionId !== uploadSessionId,
    ),
  );
}

function getPersistedAudioWorkflows(): PersistedAudioWorkflow[] {
  try {
    const raw = localStorage.getItem(ACTIVE_AUDIO_WORKFLOWS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error("Failed to read active audio workflows:", error);
    return [];
  }
}

function notifyPersistedAudioWorkflowChanged() {
  window.dispatchEvent(new CustomEvent(ACTIVE_AUDIO_WORKFLOW_EVENT));
}

function upsertPersistedAudioWorkflow(workflow: PersistedAudioWorkflow) {
  const workflows = getPersistedAudioWorkflows();
  const existingIndex = workflows.findIndex(
    (item) => item.treatmentPlanId === workflow.treatmentPlanId,
  );

  if (existingIndex >= 0) {
    workflows[existingIndex] = workflow;
  } else {
    workflows.unshift(workflow);
  }

  localStorage.setItem(ACTIVE_AUDIO_WORKFLOWS_KEY, JSON.stringify(workflows));
  notifyPersistedAudioWorkflowChanged();
}

function removePersistedAudioWorkflow(treatmentPlanId: string | number) {
  const normalizedTreatmentPlanId = String(treatmentPlanId ?? "").trim();
  if (!normalizedTreatmentPlanId) return;

  const nextWorkflows = getPersistedAudioWorkflows().filter(
    (item) => item.treatmentPlanId !== normalizedTreatmentPlanId,
  );

  localStorage.setItem(
    ACTIVE_AUDIO_WORKFLOWS_KEY,
    JSON.stringify(nextWorkflows),
  );
  notifyPersistedAudioWorkflowChanged();
}

export function getActiveAudioWorkflowForTreatmentPlan(
  treatmentPlanId: string | number,
  consentBundleId?: string | number,
) {
  const normalizedTreatmentPlanId = String(treatmentPlanId ?? "").trim();
  const normalizedConsentBundleId = String(consentBundleId ?? "").trim();
  if (!normalizedTreatmentPlanId) return null;

  const workflow = getPersistedAudioWorkflows().find(
    (item) =>
      item.treatmentPlanId === normalizedTreatmentPlanId &&
      (!normalizedConsentBundleId ||
        String(item.consentBundleId ?? "").trim() ===
          normalizedConsentBundleId),
  );

  if (!workflow) return null;

  const isStillRunning = [
    "generatingLink",
    "uploading",
    "uploadComplete",
    "transcribing",
    "transcriptionComplete",
    "generatingPatientNote",
  ].includes(workflow.stage);
  const isStale = Date.now() - workflow.updatedAtMs > 24 * 60 * 60 * 1000;

  if (!isStillRunning || isStale) return null;
  return workflow;
}

export function hasActiveAudioWorkflowForTreatmentPlan(
  treatmentPlanId: string | number,
  consentBundleId?: string | number,
) {
  return Boolean(
    getActiveAudioWorkflowForTreatmentPlan(treatmentPlanId, consentBundleId),
  );
}

function openAudioDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (!window.indexedDB) {
      reject(new Error("IndexedDB is not supported in this browser."));
      return;
    }

    const request = window.indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;

      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function saveBlobToDb(id: string, blob: Blob) {
  const db = await openAudioDb();

  return new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);

    store.put(blob, id);

    transaction.oncomplete = () => {
      db.close();
      resolve();
    };

    transaction.onerror = () => {
      db.close();
      reject(transaction.error);
    };
  });
}

async function getBlobFromDb(id: string): Promise<Blob | null> {
  const db = await openAudioDb();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readonly");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(id);

    request.onsuccess = () => {
      db.close();
      resolve(request.result || null);
    };

    request.onerror = () => {
      db.close();
      reject(request.error);
    };
  });
}

async function deleteBlobFromDb(id: string) {
  const db = await openAudioDb();

  return new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);

    store.delete(id);

    transaction.oncomplete = () => {
      db.close();
      resolve();
    };

    transaction.onerror = () => {
      db.close();
      reject(transaction.error);
    };
  });
}

async function getRecordingFile(recording: SavedRecording) {
  const blob = await getBlobFromDb(recording.id);

  if (!blob) {
    throw new Error(
      "The selected recording could not be found in local storage.",
    );
  }

  const extension = getFileExtension(
    recording.mimeType || blob.type,
    recording.name,
  );
  const safeName = recording.name.replace(/[^a-z0-9-_]+/gi, "-").toLowerCase();

  return new File([blob], `${safeName}.${extension}`, {
    type: recording.mimeType || blob.type || "audio/webm",
  });
}

async function getAudioDurationSeconds(file: Blob) {
  return new Promise<number>((resolve) => {
    const url = URL.createObjectURL(file);
    const audio = new Audio();

    const cleanup = () => URL.revokeObjectURL(url);

    audio.preload = "metadata";
    audio.onloadedmetadata = () => {
      const duration = Number.isFinite(audio.duration) ? audio.duration : 0;
      cleanup();
      resolve(Math.round(duration));
    };
    audio.onerror = () => {
      cleanup();
      resolve(0);
    };
    audio.src = url;
  });
}

function forceMp3FileName(fileName: string) {
  const cleanName = fileName
    .replace(/\.[^/.]+$/, "")
    .replace(/-webm$/i, "")
    .replace(/[^a-z0-9-_]+/gi, "-")
    .toLowerCase();

  return `${cleanName || "recording"}.mp3`;
}

function isAudioFile(file: File) {
  if (file.type?.startsWith("audio/")) return true;

  return /\.(aac|aif|aiff|flac|m4a|mp3|mp4|ogg|opus|wav|webm)$/i.test(
    file.name,
  );
}

const RetakeIcon = () => (
  <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
    <path d="M17.65 6.35A7.95 7.95 0 0 0 12 4a8 8 0 1 0 7.45 10.92.75.75 0 0 0-1.4-.54A6.5 6.5 0 1 1 12 5.5c1.75 0 3.35.7 4.53 1.83L14.5 9.36a.75.75 0 0 0 .53 1.28h5.22A.75.75 0 0 0 21 9.89V4.67a.75.75 0 0 0-1.28-.53l-2.07 2.21Z" />
  </svg>
);

function formatPatientNoteForEditor(patientNote: unknown) {
  if (patientNote === null || patientNote === undefined) return "";
  if (typeof patientNote === "string") return patientNote;

  try {
    return JSON.stringify(patientNote, null, 2);
  } catch {
    return String(patientNote);
  }
}

function escapePatientNoteHtml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function formatPatientNoteInline(value: string) {
  const escapedValue = escapePatientNoteHtml(value.trim());

  return escapedValue.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
}

function splitPatientNoteListItems(value: string) {
  const trimmedValue = value.trim();
  const hasListMarkers =
    trimmedValue.startsWith("-") ||
    /\s+-\s+(?=\*\*[^*]+:\*\*)/.test(trimmedValue);

  if (!hasListMarkers) return [];

  return trimmedValue
    .split(/\s+-\s+(?=\*\*[^*]+:\*\*)/g)
    .map((item) => item.trim())
    .filter(Boolean);
}

function normalizePatientNoteForRichEditor(value: string) {
  return value
    .replace(/\r\n?/g, "\n")
    .replace(/^\s*#\s*/, "")
    .replace(
      /\s+\*\*(O\/e|I\/O|Soft tissues|Hard tissues|Toothwear|Occlusion|Mobility|SI|SI for specific tooth|Diagnosis|Dw pt|Pt has chosen|Risk assessment):\*\*/g,
      "\n\n**$1:**",
    )
    .replace(/\s+-\s+(?=\*\*[^*]+:\*\*)/g, "\n- ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function renderPatientNoteList(items: string[]) {
  if (items.length === 0) return "";

  return `<ul>${items
    .map(
      (item) =>
        `<li>${formatPatientNoteInline(item.replace(/^-\s*/, ""))}</li>`,
    )
    .join("")}</ul>`;
}

function renderPatientNoteSection(
  label: string,
  paragraphs: string[] = [],
  listItems: string[] = [],
) {
  const bodyHtml = [
    ...paragraphs
      .map((paragraph) => paragraph.trim())
      .filter(Boolean)
      .map((paragraph) => `<p>${formatPatientNoteInline(paragraph)}</p>`),
    renderPatientNoteList(listItems),
  ]
    .filter(Boolean)
    .join("\n");

  return `<section class="audio-recorder__patientNoteSection"><h4>${escapePatientNoteHtml(
    label,
  )}</h4>${bodyHtml}</section>`;
}

function formatPatientNoteHtmlForEditor(patientNote: unknown) {
  const rawText = formatPatientNoteForEditor(patientNote);

  if (!rawText.trim()) return "";

  const normalizedText = normalizePatientNoteForRichEditor(rawText);
  const lines = normalizedText
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length === 0) return "";

  const htmlParts: string[] = [];
  let pendingListItems: string[] = [];
  let currentSectionLabel = "";
  let currentSectionParagraphs: string[] = [];
  let currentSectionListItems: string[] = [];

  const flushList = () => {
    if (pendingListItems.length === 0) return;
    htmlParts.push(renderPatientNoteList(pendingListItems));
    pendingListItems = [];
  };

  const flushSection = () => {
    if (!currentSectionLabel) return;

    htmlParts.push(
      renderPatientNoteSection(
        currentSectionLabel,
        currentSectionParagraphs,
        currentSectionListItems,
      ),
    );
    currentSectionLabel = "";
    currentSectionParagraphs = [];
    currentSectionListItems = [];
  };

  lines.forEach((line, index) => {
    const sectionMatch = line.match(/^\*\*([^*]+):\*\*\s*(.*)$/);

    if (index === 0 && !line.startsWith("-") && !sectionMatch) {
      flushSection();
      flushList();
      htmlParts.push(`<h3>${formatPatientNoteInline(line)}</h3>`);
      return;
    }

    if (sectionMatch) {
      flushSection();
      flushList();
      currentSectionLabel = sectionMatch[1];

      const body = sectionMatch[2].trim();
      const bodyListItems = splitPatientNoteListItems(body);

      if (bodyListItems.length > 0) {
        currentSectionListItems.push(...bodyListItems);
      } else if (body) {
        currentSectionParagraphs.push(body);
      }

      return;
    }

    if (line.startsWith("-")) {
      if (currentSectionLabel) {
        currentSectionListItems.push(line);
      } else {
        pendingListItems.push(line);
      }
      return;
    }

    if (currentSectionLabel) {
      currentSectionParagraphs.push(line);
      return;
    }

    flushList();
    htmlParts.push(`<p>${formatPatientNoteInline(line)}</p>`);
  });

  flushSection();
  flushList();

  return htmlParts.join("\n");
}

function normalizeEditedPatientNoteText(value: string) {
  return value
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function parseEditedPatientNote(value: string) {
  const trimmedValue = normalizeEditedPatientNoteText(value);

  if (!trimmedValue) return "";

  try {
    return JSON.parse(trimmedValue);
  } catch {
    return trimmedValue;
  }
}

function patientNoteHtmlToMarkdownText(html: string) {
  if (!html) return "";

  const container = document.createElement("div");
  container.innerHTML = html;

  const walk = (node: ChildNode): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent || "";
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return "";
    }

    const element = node as HTMLElement;
    const tagName = element.tagName.toLowerCase();
    const childText = Array.from(element.childNodes).map(walk).join("");

    if (tagName === "br") return "\n";
    if (tagName === "strong" || tagName === "b")
      return `**${childText.trim()}**`;
    if (tagName === "em" || tagName === "i") return `*${childText.trim()}*`;
    if (tagName === "li") return `- ${childText.trim()}\n`;

    if (tagName === "ul" || tagName === "ol") {
      return `${childText.trim()}\n\n`;
    }

    if (/^h[1-6]$/.test(tagName)) {
      const level = Number(tagName.slice(1));
      return `${"#".repeat(Math.min(level, 4))} ${childText.trim()}\n\n`;
    }

    if (["p", "div", "section", "blockquote"].includes(tagName)) {
      const cleanText = childText.trim();
      return cleanText ? `${cleanText}\n\n` : "";
    }

    return childText;
  };

  return normalizeEditedPatientNoteText(
    Array.from(container.childNodes).map(walk).join(""),
  );
}

function getPatientNoteCKEditorConfig(minHeight = 360) {
  const CKEDITOR = (window as any).CKEDITOR;

  return {
    allowedContent: true,
    removePlugins: "resize,elementspath",
    resize_enabled: false,
    height: minHeight,
    extraAllowedContent:
      "section h1 h2 h3 h4 h5 h6 p br ul ol li strong em b i blockquote; *(*)[id,class,style]",
    enterMode: CKEDITOR?.ENTER_P,
    shiftEnterMode: CKEDITOR?.ENTER_BR,
    toolbar: [
      {
        name: "basicstyles",
        items: ["Bold", "Italic", "Underline", "RemoveFormat"],
      },
      {
        name: "paragraph",
        items: ["BulletedList", "NumberedList", "Blockquote"],
      },
      { name: "links", items: ["Link", "Unlink"] },
      { name: "clipboard", items: ["Undo", "Redo"] },
    ],
  };
}

function patientNoteHtmlToPlainText(html: string) {
  if (!html) return "";

  const container = document.createElement("div");
  container.innerHTML = html;

  const walk = (node: ChildNode): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent || "";
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return "";
    }

    const element = node as HTMLElement;
    const tagName = element.tagName.toLowerCase();
    const childText = Array.from(element.childNodes).map(walk).join("");

    if (tagName === "br") return "\n";

    if (tagName === "li") {
      const parentTag = element.parentElement?.tagName.toLowerCase();

      if (parentTag === "ol") {
        const listItems = Array.from(element.parentElement?.children || []);
        return `${listItems.indexOf(element) + 1}. ${childText.trim()}\n`;
      }

      return `- ${childText.trim()}\n`;
    }

    if (["ul", "ol"].includes(tagName)) {
      return `${childText.trim()}\n\n`;
    }

    if (
      /^h[1-6]$/.test(tagName) ||
      ["p", "div", "section", "blockquote"].includes(tagName)
    ) {
      const cleanText = childText.trim();
      return cleanText ? `${cleanText}\n\n` : "";
    }

    return childText;
  };

  return normalizeEditedPatientNoteText(
    Array.from(container.childNodes).map(walk).join(""),
  );
}

function buildPatientNoteClipboardHtml(html: string) {
  const wrapper = document.createElement("div");
  wrapper.innerHTML = html;
  wrapper.style.fontFamily = "Arial, Helvetica, sans-serif";
  wrapper.style.fontSize = "14px";
  wrapper.style.lineHeight = "1.55";
  wrapper.style.color = "#111827";

  wrapper.querySelectorAll<HTMLElement>("h1, h2, h3, h4, h5, h6").forEach(
    (heading) => {
      heading.style.fontWeight = "700";
      heading.style.margin = "16px 0 8px";
      heading.style.lineHeight = "1.3";
    },
  );

  wrapper.querySelectorAll<HTMLElement>("h3").forEach((heading) => {
    heading.style.fontSize = "20px";
  });

  wrapper.querySelectorAll<HTMLElement>("h4").forEach((heading) => {
    heading.style.fontSize = "16px";
  });

  wrapper.querySelectorAll<HTMLElement>("p").forEach((paragraph) => {
    paragraph.style.margin = "0 0 10px";
  });

  wrapper.querySelectorAll<HTMLElement>("ul, ol").forEach((list) => {
    list.style.margin = "0 0 12px";
    list.style.paddingLeft = "24px";
  });

  wrapper.querySelectorAll<HTMLElement>("li").forEach((item) => {
    item.style.margin = "0 0 6px";
  });

  return wrapper.outerHTML;
}

async function copyRichTextToClipboard(html: string, plainText: string) {
  const richHtml = buildPatientNoteClipboardHtml(html);
  const ClipboardItemConstructor = (window as any).ClipboardItem;

  if (navigator.clipboard?.write && ClipboardItemConstructor) {
    try {
      const clipboardItem = new ClipboardItemConstructor({
        "text/html": new Blob([richHtml], { type: "text/html" }),
        "text/plain": new Blob([plainText], { type: "text/plain" }),
      });

      await navigator.clipboard.write([clipboardItem]);
      return;
    } catch (error) {
      console.warn(
        "Rich clipboard copy failed. Trying the browser fallback.",
        error,
      );
    }
  }

  const copyContainer = document.createElement("div");
  copyContainer.innerHTML = richHtml;
  copyContainer.contentEditable = "true";
  copyContainer.style.position = "fixed";
  copyContainer.style.top = "0";
  copyContainer.style.left = "-9999px";
  copyContainer.style.opacity = "0";
  copyContainer.setAttribute("aria-hidden", "true");

  document.body.appendChild(copyContainer);

  const selection = window.getSelection();
  const previousRanges = selection
    ? Array.from({ length: selection.rangeCount }, (_, index) =>
        selection.getRangeAt(index).cloneRange(),
      )
    : [];
  const range = document.createRange();

  range.selectNodeContents(copyContainer);
  selection?.removeAllRanges();
  selection?.addRange(range);

  try {
    const copied = document.execCommand("copy");

    if (!copied) {
      throw new Error("Copy command was blocked by the browser.");
    }
  } finally {
    selection?.removeAllRanges();
    previousRanges.forEach((previousRange) =>
      selection?.addRange(previousRange),
    );
    document.body.removeChild(copyContainer);
  }
}

const AudioRecorder: React.FC<AudioRecorderProps> = ({
  consentBundleId,
  treatmentPlanId,
  initialAudioRecordData,
  apiBaseUrl = "",
  uploadEnabled = true,
  uploadDisabledMessage = "Add a treatment ID before uploading audio.",
  recordDisabled = false,
  recordDisabledLabel = "Record",
  recordDisabledMessage = "Please wait before starting the recording.",
  skipTreatmentPlanCheckOnRecord = false,
  matchConsentBundleId = true,
  onCreateConsentBundleIfMissing,
  onTreatmentPlanCheckPassed,
  maxRecordings = DEFAULT_MAX_RECORDINGS,
  retryWindowMinutes = DEFAULT_RETRY_WINDOW_MINUTES,
  maxUploadAttempts = DEFAULT_MAX_UPLOAD_ATTEMPTS,
  retryPollMs = DEFAULT_RETRY_POLL_MS,
  authToken,
  onUploadComplete,
  onUploadFailed,
  onRequestClose,
  onUnsafeStateChange,
  onPatientNoteReady,
  onPatientNoteSaved,
}) => {
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const previewStreamRef = useRef<MediaStream | null>(null);
  const micTestStreamRef = useRef<MediaStream | null>(null);
  const fullRecordingPartsRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const recordingSecondsRef = useRef<number>(0);
  const audioContextRef = useRef<AudioContext | null>(null);
  const micTestAudioContextRef = useRef<AudioContext | null>(null);
  const volumeTimerRef = useRef<number | null>(null);
  const micTestTimerRef = useRef<number | null>(null);
  const retryTimerRef = useRef<number | null>(null);
  const isRetryingPendingUploadsRef = useRef(false);
  const audioRefs = useRef<Record<string, HTMLAudioElement | null>>({});
  const objectUrlsRef = useRef<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const debugS3FileInputRef = useRef<HTMLInputElement | null>(null);
  const patientNoteEditorRef = useRef<HTMLDivElement | null>(null);
  const patientNoteTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const patientNoteCkEditorRef = useRef<any>(null);
  const patientNoteCkScrollCleanupRef = useRef<(() => void) | null>(null);
  const patientNoteCopyTimerRef = useRef<number | null>(null);
  const patientNoteReviewUserInteractedRef = useRef(false);
  const activeAudioWorkflowTokenRef = useRef(0);
  const activeAudioUploadAbortRef = useRef<AbortController | null>(null);
  const activePersistedWorkflowRef = useRef<PersistedAudioWorkflow | null>(
    getActiveAudioWorkflowForTreatmentPlan(treatmentPlanId, consentBundleId),
  );
  const shouldRestorePersistedWorkflowRef = useRef(
    Boolean(activePersistedWorkflowRef.current),
  );
  const persistedProcessingResumeStartedRef = useRef(false);
  const initialGeneratedPatientNoteOpenedRef = useRef(false);
  const treatmentPlanCheckRequestRef = useRef<{
    planId: string;
    promise: Promise<boolean>;
  } | null>(null);
  const pendingExistingAudioConfirmRef = useRef<(() => void) | null>(null);
  const shouldUploadAfterStopRef = useRef(false);
  const pendingUnsafeCloseRef = useRef(false);
  const unsafeCloseChoicePendingRef = useRef(false);
  const resumeRecordingAfterUnsafeCloseCancelRef = useRef(false);
  const discardRecordingAfterStopRef = useRef(false);
  const restartRecordingAfterDiscardRef = useRef(false);
  const speakingPromptTimerRef = useRef<number | null>(null);
  const micCoachTimerRef = useRef<number | null>(null);
  const onUnsafeStateChangeRef =
    useRef<AudioRecorderProps["onUnsafeStateChange"]>(onUnsafeStateChange);
  const lastUnsafeStateRef = useRef<AudioUnsafeCloseState | null>(null);

  onUnsafeStateChangeRef.current = onUnsafeStateChange;

  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [status, setStatus] = useState("Ready to record");
  const [recordings, setRecordings] = useState<SavedRecording[]>([]);
  const [selectedRecordingId, setSelectedRecordingId] = useState("");
  const [audioInputs, setAudioInputs] = useState<AudioInputDevice[]>([]);
  const [selectedMicId, setSelectedMicId] = useState("");
  const [savedMicId, setSavedMicId] = useState(
    () => getStoredMicPreference()?.deviceId || "",
  );
  const [testingMicId, setTestingMicId] = useState("");
  const [selectedMicLabel, setSelectedMicLabel] =
    useState("Default microphone");
  const [micLevel, setMicLevel] = useState(0);
  const [micTestLevel, setMicTestLevel] = useState(0);
  const [isMicPreviewActive, setIsMicPreviewActive] = useState(false);
  const [showMicSettings, setShowMicSettings] = useState(false);
  const [micCoachNotification, setMicCoachNotification] = useState("");
  const [showSpeakingPrompt, setShowSpeakingPrompt] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState | null>(
    null,
  );
  const [activePlaybackId, setActivePlaybackId] = useState<string | null>(null);
  const [playerStates, setPlayerStates] = useState<Record<string, PlayerState>>(
    {},
  );
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [isPreviewDraggingFile, setIsPreviewDraggingFile] = useState(false);
  const [isDropBoxOpen, setIsDropBoxOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [workflowOverlay, setWorkflowOverlay] =
    useState<AudioWorkflowOverlayState>(() => {
      const persistedWorkflow = activePersistedWorkflowRef.current;

      if (persistedWorkflow) {
        return {
          isOpen: true,
          stage: persistedWorkflow.stage,
          progress: persistedWorkflow.progress,
          currentStatus:
            persistedWorkflow.currentStatus || "Restoring audio processing...",
          retryAction: persistedWorkflow.retryAction,
          failedUploadSessionId:
            persistedWorkflow.retryAction === "transcription"
              ? persistedWorkflow.uploadSessionId
              : undefined,
        };
      }

      return {
        isOpen: false,
        stage: "idle",
        progress: 0,
      };
    });
  const [patientNoteEditor, setPatientNoteEditor] =
    useState<PatientNoteEditorState>({
      isOpen: false,
      uploadSessionId: "",
      audioId: "",
      patientNoteText: "",
      patientNoteHtml: "",
      originalPatientNote: null,
      status: "",
      isSaving: false,
    });
  const [isPatientNoteCopied, setIsPatientNoteCopied] = useState(false);
  const [isPatientNoteReviewedToBottom, setIsPatientNoteReviewedToBottom] =
    useState(false);
  const [isCheckingTreatmentPlanAudio, setIsCheckingTreatmentPlanAudio] =
    useState(false);
  const [isCreatingConsentBundle, setIsCreatingConsentBundle] = useState(false);
  const [isDebugS3DraggingFile, setIsDebugS3DraggingFile] = useState(false);
  const [debugS3Upload, setDebugS3Upload] = useState<DebugS3UploadState>({
    status: "idle",
    fileName: "",
    progress: 0,
    uploadSessionId: "",
    uploadedParts: 0,
    totalParts: 0,
    errorMessage: "",
  });

  const selectedRecording = useMemo(
    () => recordings.find((recording) => recording.id === selectedRecordingId),
    [recordings, selectedRecordingId],
  );

  const workflowFailedRecording = useMemo(
    () =>
      workflowOverlay.failedRecordingId
        ? recordings.find(
            (recording) => recording.id === workflowOverlay.failedRecordingId,
          )
        : selectedRecording,
    [recordings, selectedRecording, workflowOverlay.failedRecordingId],
  );

  const previewRecording = selectedRecording || recordings[0];
  const previewPlayerState = previewRecording
    ? playerStates[previewRecording.id] || {
        currentTime: 0,
        duration: previewRecording.durationSeconds || 0,
      }
    : null;
  const previewDuration = previewRecording
    ? previewPlayerState?.duration || previewRecording.durationSeconds || 0
    : 0;
  const previewProgress =
    previewRecording && previewDuration
      ? Math.min(
          100,
          ((previewPlayerState?.currentTime || 0) / previewDuration) * 100,
        )
      : 0;
  const isPreviewPlaying = previewRecording
    ? activePlaybackId === previewRecording.id
    : false;
  const previewStatusLabel = previewRecording
    ? previewRecording.uploadStatus === "uploaded"
      ? "Uploaded"
      : previewRecording.uploadStatus === "uploading"
        ? `Uploading ${previewRecording.uploadProgress || 0}%`
        : previewRecording.uploadStatus === "queued"
          ? "Retry queued"
          : previewRecording.uploadStatus === "failed"
            ? "Failed"
            : previewRecording.uploadStatus === "expired"
              ? "Expired"
              : "Ready to preview"
    : "Drop audio";

  const localRecordingLimit = Math.min(Math.max(maxRecordings || 1, 1), 1);

  const isActivelyRecording = isRecording && !isPaused;
  const hasLiveInput = isActivelyRecording && micLevel >= 3;
  const hasNoMicInput = isActivelyRecording && !hasLiveInput;

  const workflowStageMeta = AUDIO_WORKFLOW_STAGE_META[workflowOverlay.stage];
  const workflowProgress = Math.max(
    0,
    Math.min(100, Math.round(workflowOverlay.progress || 0)),
  );
  const workflowProgressWidth = getWorkflowVisualProgress(
    workflowOverlay.stage,
    workflowProgress,
  );
  const workflowSimpleStatus =
    AUDIO_WORKFLOW_SIMPLE_STATUS[workflowOverlay.stage];
  const workflowProgressText =
    workflowOverlay.stage === "failed"
      ? "Failed"
      : workflowOverlay.currentStatus
        ? workflowOverlay.currentStatus
        : workflowOverlay.stage === "uploading"
          ? `${workflowSimpleStatus} ${workflowProgress}%`
          : workflowSimpleStatus;
  const workflowProgressIsIndeterminate = isWorkflowIndeterminateStage(
    workflowOverlay.stage,
  );
  const isUploadInProgress = recordings.some(
    (recording) =>
      recording.uploadStatus === "uploading" ||
      recording.uploadStatus === "queued",
  );

  const hasValidTreatmentPlanId =
    String(treatmentPlanId || "").trim().length > 0;
  const canUploadAndTranscribe = uploadEnabled && hasValidTreatmentPlanId;

  const isWorkflowInProgress =
    workflowOverlay.isOpen &&
    [
      "generatingLink",
      "uploading",
      "uploadComplete",
      "transcribing",
      "transcriptionComplete",
      "generatingPatientNote",
    ].includes(workflowOverlay.stage);
  const shouldConfirmUnsafeExit =
    isRecording || isUploadInProgress || isWorkflowInProgress;
  const unsafeExitReason: AudioUnsafeCloseState["reason"] = isRecording
    ? "recording"
    : isUploadInProgress
      ? "uploading"
      : isWorkflowInProgress
        ? "processing"
        : null;
  const unsafeExitTitle = isRecording
    ? "Recording in progress"
    : "Audio upload in progress";
  const unsafeExitMessage = isRecording
    ? "Your recording has been paused while you choose whether to continue recording or close the recorder."
    : "Audio upload or processing is still running. Closing now can interrupt the workflow. Stay on this screen until it finishes.";

  const getWorkflowRequestHeaders = (json = true) => {
    const axiosAuthorization = axios.defaults.headers.common.Authorization;
    const fallbackToken = authToken || getDefaultAuthToken();
    const headers: Record<string, string> = {};

    if (json) headers["Content-Type"] = "application/json";

    if (axiosAuthorization) {
      headers.Authorization = String(axiosAuthorization);
    } else if (fallbackToken) {
      headers.Authorization = fallbackToken.startsWith("Bearer ")
        ? fallbackToken
        : `Bearer ${fallbackToken}`;
    }

    return headers;
  };

  const openWorkflowOverlay = (
    stage: AudioWorkflowStage,
    progress?: number,
    errorMessage?: string,
    failedRecordingId?: string,
    currentStatus?: string,
    retryAction?: "transcription",
    failedUploadSessionId?: string,
  ) => {
    const nextOverlay: AudioWorkflowOverlayState = {
      isOpen: true,
      stage,
      progress,
      currentStatus,
      errorMessage,
      failedRecordingId,
      retryAction,
      failedUploadSessionId,
    };

    setWorkflowOverlay(nextOverlay);

    if (activePersistedWorkflowRef.current) {
      const nextPersistedWorkflow: PersistedAudioWorkflow = {
        ...activePersistedWorkflowRef.current,
        stage,
        progress,
        currentStatus,
        retryAction,
        updatedAtMs: Date.now(),
      };
      activePersistedWorkflowRef.current = nextPersistedWorkflow;
      upsertPersistedAudioWorkflow(nextPersistedWorkflow);
    }
  };

  const closeWorkflowOverlay = () => {
    setWorkflowOverlay({
      isOpen: false,
      stage: "idle",
      progress: 0,
      currentStatus: undefined,
      errorMessage: undefined,
      failedRecordingId: undefined,
      retryAction: undefined,
      failedUploadSessionId: undefined,
    });
  };

  const beginFreshAudioWorkflow = () => {
    activeAudioUploadAbortRef.current?.abort();
    activeAudioWorkflowTokenRef.current += 1;
    activeAudioUploadAbortRef.current = new AbortController();
    return activeAudioWorkflowTokenRef.current;
  };

  const isCurrentAudioWorkflow = (workflowToken: number) =>
    activeAudioWorkflowTokenRef.current === workflowToken;

  const activatePersistedAudioWorkflow = (
    job: PendingUploadJob,
    stage: AudioWorkflowStage,
    currentStatus?: string,
  ) => {
    const persistedWorkflow: PersistedAudioWorkflow = {
      treatmentPlanId: String(
        job.treatmentPlanId || treatmentPlanId || "",
      ).trim(),
      consentBundleId: job.consentBundleId || consentBundleId,
      uploadSessionId: job.uploadSessionId,
      recordingId: job.recordingId,
      stage,
      currentStatus,
      updatedAtMs: Date.now(),
    };

    if (!persistedWorkflow.treatmentPlanId) return;

    activePersistedWorkflowRef.current = persistedWorkflow;
    upsertPersistedAudioWorkflow(persistedWorkflow);
  };

  const clearActivePersistedAudioWorkflow = () => {
    removePersistedAudioWorkflow(treatmentPlanId);
    activePersistedWorkflowRef.current = null;
  };

  const createAudioReplacedError = () => {
    const error = new Error("Audio upload replaced by a newer file.");
    error.name = "AudioUploadReplacedError";
    return error;
  };

  const isAudioReplacedError = (error: any) =>
    error?.name === "AudioUploadReplacedError" ||
    error?.name === "AbortError" ||
    error?.code === "ERR_CANCELED" ||
    error?.message === "Audio upload replaced by a newer file." ||
    error?.message === "canceled";

  const createTranscriptionRetryError = (
    message: string,
    uploadSessionId: string,
  ) => {
    const error = new Error(message) as Error & { uploadSessionId?: string };
    error.name = "TranscriptionRetryError";
    error.uploadSessionId = uploadSessionId;
    return error;
  };

  const isTranscriptionRetryError = (
    error: any,
  ): error is Error & { uploadSessionId: string } =>
    error?.name === "TranscriptionRetryError" &&
    Boolean(String(error?.uploadSessionId || "").trim());

  const showTranscriptionRetryFailure = (
    error: Error & { uploadSessionId: string },
    failedRecordingId?: string,
  ) => {
    const message =
      error.message ||
      "Transcription failed. Click Retry transcription to try again.";

    openWorkflowOverlay(
      "failed",
      100,
      message,
      failedRecordingId,
      "Transcription failed",
      "transcription",
      error.uploadSessionId,
    );
    setStatus(message);
  };

  const cancelCurrentAudioWorkflowForReplacement = () => {
    activeAudioUploadAbortRef.current?.abort();
    activeAudioWorkflowTokenRef.current += 1;
    activeAudioUploadAbortRef.current = null;
    savePendingUploadJobs([]);
    closeWorkflowOverlay();
    setPatientNoteEditor((prev) => ({
      ...prev,
      isOpen: false,
      isSaving: false,
      errorMessage: undefined,
    }));
    setIsPatientNoteCopied(false);
    setIsPatientNoteReviewedToBottom(false);
  };

  const getNormalizedTreatmentPlanId = () =>
    String(treatmentPlanId ?? "").trim();

  const getShouldShowExistingAudioWarning = (
    response: TreatmentPlanAudioCheckResponse | boolean | null | undefined,
  ) => {
    if (response === false) return true;
    if (!response || typeof response !== "object") return false;

    return (
      response.result === false ||
      response.exists === true ||
      response.has_recording === true ||
      response.has_audio_recording === true
    );
  };

  const checkTreatmentPlanAudioExists = async (planId: string) => {
    const activeRequest = treatmentPlanCheckRequestRef.current;
    if (activeRequest?.planId === planId) return activeRequest.promise;

    const request = axios
      .get<TreatmentPlanAudioCheckResponse | boolean>(
        buildApiUrl(
          apiBaseUrl,
          `/audio-recording/check_treatment_plan_id/${encodeURIComponent(planId)}`,
        ),
        {
          withCredentials: false,
          headers: getWorkflowRequestHeaders(false),
          validateStatus: () => true,
        },
      )
      .then(({ data }) => {
        if (data && typeof data === "object") {
          const responseMessage = data.detail || data.error;

          if (responseMessage) {
            throw new Error(String(responseMessage));
          }
        }

        return getShouldShowExistingAudioWarning(data);
      });

    treatmentPlanCheckRequestRef.current = { planId, promise: request };

    try {
      return await request;
    } finally {
      if (treatmentPlanCheckRequestRef.current?.promise === request) {
        treatmentPlanCheckRequestRef.current = null;
      }
    }
  };

  const createUploadCompletionError = (message: string) => {
    const error = new Error(message);
    error.name = "UploadCompletionError";
    return error;
  };

  const openExistingAudioWarning = (planId: string, onConfirm?: () => void) => {
    pendingExistingAudioConfirmRef.current = onConfirm || null;
    setConfirmModal({
      action: "replaceExistingAudio",
      title: "Existing recording found",
      message: `A recording already exists for Treatment Plan ID ${planId}. Continuing will replace the old audio, transcript, and patient note. Do you want to continue?`,
      confirmLabel: "Yes, continue",
      cancelLabel: "Cancel",
      danger: true,
      details: [
        "This action cannot be undone after the new recording is processed.",
      ],
    });
  };

  const generateTranscriptAndPatientNote = async (
    uploadSessionId: string,
    signal?: AbortSignal,
  ) => {
    const { data } = await axios.post<AudioRecordDataResponse>(
      buildApiUrl(
        apiBaseUrl,
        `${GENERATE_TRANSCRIPT_AND_PATIENT_NOTE_PATH}/${encodeURIComponent(
          uploadSessionId,
        )}`,
      ),
      null,
      {
        withCredentials: false,
        headers: getWorkflowRequestHeaders(false),
        signal,
      },
    );

    return data;
  };

  const getAudioRecordData = async (signal?: AbortSignal) => {
    const normalizedTreatmentPlanId = getNormalizedTreatmentPlanId();

    if (!normalizedTreatmentPlanId) {
      throw new Error("Missing treatment plan ID for audio status polling.");
    }

    const { data } = await axios.get<AudioRecordDataResponse>(
      buildApiUrl(
        apiBaseUrl,
        `${GET_AUDIO_RECORD_DATA_PATH}/${encodeURIComponent(
          normalizedTreatmentPlanId,
        )}`,
      ),
      {
        withCredentials: false,
        headers: getWorkflowRequestHeaders(false),
        signal,
      },
    );

    return data;
  };

  const openPatientNoteEditorFromResponse = (
    job: Pick<PendingUploadJob, "uploadSessionId">,
    patientNoteResponse: GeneratePatientNoteResponse,
  ) => {
    const patientNoteContent =
      patientNoteResponse?.patient_note ??
      patientNoteResponse?.patient_note_body;
    const patientNoteText = formatPatientNoteForEditor(patientNoteContent);
    const patientNoteHtml = formatPatientNoteHtmlForEditor(patientNoteContent);

    const audioId = String(patientNoteResponse?.audio_id || "").trim();
    const uploadSessionId = String(
      patientNoteResponse?.upload_session_id || job.uploadSessionId || "",
    ).trim();

    const payload: PatientNoteReadyPayload = {
      uploadSessionId,
      status: patientNoteResponse?.status || "generated",
      patientNote: patientNoteContent ?? null,
      patientNoteText,
      treatmentPlanId,
      consentBundleId,
      response: patientNoteResponse,
    };

    closeWorkflowOverlay();
    setStatus("Patient note generated. Review and save the note.");

    if (onPatientNoteReady) {
      onPatientNoteReady(payload);
      onRequestClose?.();
      return;
    }

    setIsPatientNoteCopied(false);
    patientNoteReviewUserInteractedRef.current = false;
    setIsPatientNoteReviewedToBottom(false);
    setPatientNoteEditor({
      isOpen: true,
      uploadSessionId: payload.uploadSessionId,
      audioId,
      patientNoteText,
      patientNoteHtml,
      originalPatientNote: payload.patientNote,
      status: payload.status,
      isSaving: false,
      errorMessage: undefined,
    });
  };

  useEffect(() => {
    if (!initialAudioRecordData) return;
    if (initialGeneratedPatientNoteOpenedRef.current) return;
    if (
      !hasGeneratedPatientNoteInAudioRecordData(
        initialAudioRecordData,
        treatmentPlanId,
        consentBundleId,
        matchConsentBundleId,
      )
    ) {
      return;
    }

    const snapshot = getAudioProcessingSnapshot(initialAudioRecordData);
    const patientNoteText = formatPatientNoteForEditor(
      snapshot.patientNote,
    ).trim();

    if (snapshot.patientNoteState !== "completed" || !patientNoteText) {
      return;
    }

    const persistedWorkflow = getPersistedAudioWorkflows().find(
      (workflow) =>
        workflow.treatmentPlanId === String(treatmentPlanId ?? "").trim() &&
        String(workflow.consentBundleId ?? "").trim() ===
          String(consentBundleId ?? "").trim(),
    );
    const uploadSessionId =
      getUploadSessionIdFromAudioRecordData(initialAudioRecordData) ||
      String(persistedWorkflow?.uploadSessionId || "").trim();
    const audioId = getFirstAudioRecordValue(initialAudioRecordData, [
      "audio_id",
      "audioId",
      "id",
    ]);

    if (persistedWorkflow) {
      upsertPersistedAudioWorkflow({
        ...persistedWorkflow,
        stage: "patientNoteComplete",
        progress: 100,
        currentStatus: "Patient note generated",
        updatedAtMs: Date.now(),
      });
      activePersistedWorkflowRef.current = null;
    }

    initialGeneratedPatientNoteOpenedRef.current = true;
    openPatientNoteEditorFromResponse(
      { uploadSessionId },
      {
        upload_session_id: uploadSessionId,
        audio_id:
          typeof audioId === "number" || typeof audioId === "string"
            ? audioId
            : undefined,
        status: String(snapshot.patientNoteStatus || "generated"),
        patient_note: snapshot.patientNote,
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAudioRecordData]);

  const runTranscriptionAndPatientNoteFlow = async (
    job: PendingUploadJob,
    completeResponse: CompleteUploadResponse,
    options?: {
      workflowToken?: number;
      signal?: AbortSignal;
      resume?: boolean;
      pollImmediately?: boolean;
    },
  ) => {
    const workflowToken =
      options?.workflowToken ?? activeAudioWorkflowTokenRef.current;
    const assertCurrentWorkflow = () => {
      if (
        activeAudioWorkflowTokenRef.current !== workflowToken ||
        options?.signal?.aborted
      ) {
        const error = new Error("Audio upload replaced by a newer file.");
        error.name = "AudioUploadReplacedError";
        throw error;
      }
    };

    activatePersistedAudioWorkflow(
      job,
      options?.resume ? "transcribing" : "uploadComplete",
      options?.resume
        ? "Restoring audio processing..."
        : "Audio upload completed",
    );

    if (!options?.resume) {
      assertCurrentWorkflow();
      openWorkflowOverlay("uploadComplete", 100);
      await wait(AUDIO_WORKFLOW_SUCCESS_PAUSE_MS);
    }

    assertCurrentWorkflow();
    openWorkflowOverlay("transcribing");
    setStatus("Upload completed. Checking transcription status...");
    console.log("Starting get_audio_record_data polling:", {
      uploadSessionId:
        completeResponse?.upload_session_id || job.uploadSessionId,
      treatmentPlanId: getNormalizedTreatmentPlanId(),
      status: completeResponse?.status,
    });

    let transcriptCompletedWasShown = false;
    let patientNoteFailureRetrySent = false;
    let consecutiveStatusCheckFailures = 0;
    let consecutiveMismatchedResponses = 0;

    for (
      let pollCount = 0;
      pollCount < AUDIO_WORKFLOW_MAX_POLLS;
      pollCount += 1
    ) {
      assertCurrentWorkflow();
      if (!options?.pollImmediately || pollCount > 0) {
        await wait(AUDIO_WORKFLOW_POLL_MS);
      }
      assertCurrentWorkflow();

      let audioRecordResponse: AudioRecordDataResponse;

      try {
        audioRecordResponse = await getAudioRecordData(options?.signal);
      } catch (pollingError: any) {
        if (options?.signal?.aborted || isAudioReplacedError(pollingError)) {
          throw pollingError;
        }

        consecutiveStatusCheckFailures += 1;

        console.warn(
          "get_audio_record_data failed. Retrying the same polling request in 5 seconds:",
          pollingError,
        );

        if (consecutiveStatusCheckFailures >= 3) {
          throw createTranscriptionRetryError(
            "The processing status could not be checked after 3 attempts. Click Retry transcription to restart the process.",
            job.uploadSessionId,
          );
        }

        openWorkflowOverlay(
          transcriptCompletedWasShown
            ? "generatingPatientNote"
            : "transcribing",
          undefined,
          undefined,
          undefined,
          "Status check failed. Retrying in 5 seconds...",
        );
        setStatus(
          "Could not retrieve the audio processing status. Retrying the same status request in 5 seconds...",
        );
        continue;
      }

      assertCurrentWorkflow();
      consecutiveStatusCheckFailures = 0;

      if (
        !doesAudioRecordMatchContext(
          audioRecordResponse,
          treatmentPlanId,
          consentBundleId,
          matchConsentBundleId,
        )
      ) {
        consecutiveMismatchedResponses += 1;
        console.warn("Ignored mismatched audio processing response:", {
          expectedTreatmentPlanId: String(treatmentPlanId),
          expectedConsentBundleId: String(consentBundleId),
          responseTreatmentPlanId: getFirstAudioRecordValue(
            audioRecordResponse,
            ["treatment_plan_id", "treatmentPlanId"],
          ),
          responseConsentBundleId: getFirstAudioRecordValue(
            audioRecordResponse,
            ["consent_bundle_id", "consentBundleId"],
          ),
        });
        openWorkflowOverlay(
          transcriptCompletedWasShown
            ? "generatingPatientNote"
            : "transcribing",
          undefined,
          undefined,
          undefined,
          "Waiting for the matching treatment-plan audio",
        );

        if (consecutiveMismatchedResponses >= 12) {
          throw createTranscriptionRetryError(
            "No matching treatment-plan audio was returned after 60 seconds. Click Retry transcription to restart the process.",
            job.uploadSessionId,
          );
        }

        continue;
      }

      consecutiveMismatchedResponses = 0;

      const snapshot = getAudioProcessingSnapshot(audioRecordResponse);
      const normalizedPatientNoteStatus = normalizeWorkflowStatus(
        snapshot.patientNoteStatus,
      ).replace(/[\s-]+/g, "_");

      console.log("Audio processing status:", {
        treatmentPlanId: getNormalizedTreatmentPlanId(),
        transcriptStatus: snapshot.transcriptStatus,
        patientNoteStatus: snapshot.patientNoteStatus,
        transcriptState: snapshot.transcriptState,
        patientNoteState: snapshot.patientNoteState,
      });

      if (normalizedPatientNoteStatus === "edited") {
        clearActivePersistedAudioWorkflow();
        closeWorkflowOverlay();
        setStatus("Patient note has already been reviewed and edited.");
        onRequestClose?.();
        return;
      }

      if (snapshot.transcriptState === "failed") {
        throw createTranscriptionRetryError(
          "Transcription failed. Click Retry transcription to try again.",
          job.uploadSessionId,
        );
      }

      if (
        snapshot.transcriptState === "unknown" ||
        snapshot.transcriptState === "pending" ||
        snapshot.transcriptState === "processing"
      ) {
        openWorkflowOverlay(
          "transcribing",
          undefined,
          undefined,
          undefined,
          "Transcription in progress",
        );
        setStatus("Transcribing audio...");
        continue;
      }

      if (!transcriptCompletedWasShown) {
        transcriptCompletedWasShown = true;
        openWorkflowOverlay("transcriptionComplete", 100);
        setStatus("Transcription completed. Generating patient note...");
        await wait(AUDIO_WORKFLOW_SUCCESS_PAUSE_MS);
        assertCurrentWorkflow();
      }

      if (snapshot.patientNoteState === "failed") {
        openWorkflowOverlay(
          "generatingPatientNote",
          undefined,
          undefined,
          undefined,
          "Patient-note generation failed. Retrying...",
        );
        setStatus("Patient-note generation failed. Retrying automatically...");

        if (!patientNoteFailureRetrySent) {
          patientNoteFailureRetrySent = true;
          await generateTranscriptAndPatientNote(
            job.uploadSessionId,
            options?.signal,
          );
        }

        continue;
      }

      if (
        snapshot.patientNoteState === "completed" &&
        normalizedPatientNoteStatus === "generated"
      ) {
        patientNoteFailureRetrySent = false;
        const patientNoteResponse: GeneratePatientNoteResponse = {
          upload_session_id: job.uploadSessionId,
          status: String(snapshot.patientNoteStatus || "generated"),
          patient_note: snapshot.patientNote,
        };

        openWorkflowOverlay("patientNoteComplete", 100);
        setStatus("Patient note generated.");
        await wait(AUDIO_WORKFLOW_SUCCESS_PAUSE_MS);
        assertCurrentWorkflow();
        openPatientNoteEditorFromResponse(job, patientNoteResponse);
        return;
      }

      openWorkflowOverlay(
        "generatingPatientNote",
        undefined,
        undefined,
        undefined,
        "Patient-note generation in progress",
      );
      setStatus("Generating patient note...");
    }

    throw new Error(
      "Audio processing timed out while waiting for transcription and patient-note generation.",
    );
  };

  const resumePersistedAudioWorkflow = async () => {
    const persistedWorkflow = getActiveAudioWorkflowForTreatmentPlan(
      treatmentPlanId,
      consentBundleId,
    );

    if (!persistedWorkflow) {
      await retryPendingUploads();
      return;
    }

    activePersistedWorkflowRef.current = persistedWorkflow;
    setWorkflowOverlay({
      isOpen: true,
      stage: persistedWorkflow.stage,
      progress: persistedWorkflow.progress,
      currentStatus:
        persistedWorkflow.currentStatus || "Restoring audio processing...",
      retryAction: persistedWorkflow.retryAction,
      failedUploadSessionId:
        persistedWorkflow.retryAction === "transcription"
          ? persistedWorkflow.uploadSessionId
          : undefined,
    });

    if (
      persistedWorkflow.stage === "failed" &&
      persistedWorkflow.retryAction === "transcription"
    ) {
      return;
    }

    const pendingUploadJob = getPendingUploadJobs().find(
      (job) => job.uploadSessionId === persistedWorkflow.uploadSessionId,
    );

    if (
      pendingUploadJob &&
      ["queued", "uploading", "finalizing", "failed"].includes(
        pendingUploadJob.status,
      )
    ) {
      await retryPendingUploads();
      return;
    }

    if (persistedProcessingResumeStartedRef.current) return;
    persistedProcessingResumeStartedRef.current = true;

    const workflowToken = beginFreshAudioWorkflow();
    activePersistedWorkflowRef.current = persistedWorkflow;
    const resumedJob: PendingUploadJob = {
      recordingId: persistedWorkflow.recordingId,
      consentBundleId: persistedWorkflow.consentBundleId,
      treatmentPlanId: persistedWorkflow.treatmentPlanId,
      uploadSessionId: persistedWorkflow.uploadSessionId,
      fileName: "",
      mimeType: "audio/webm",
      size: 0,
      durationSeconds: 0,
      recordedAt: new Date(persistedWorkflow.updatedAtMs).toISOString(),
      partSize: 0,
      urls: [],
      attempts: 0,
      maxAttempts: maxUploadAttempts,
      createdAtMs: persistedWorkflow.updatedAtMs,
      expiresAtMs: Date.now() + retryWindowMinutes * 60 * 1000,
      uploadedParts: [],
      status: "completed",
    };

    try {
      await runTranscriptionAndPatientNoteFlow(
        resumedJob,
        {
          upload_session_id: persistedWorkflow.uploadSessionId,
          status: "uploaded",
        },
        {
          workflowToken,
          signal: activeAudioUploadAbortRef.current?.signal,
          resume: true,
          pollImmediately: true,
        },
      );
    } catch (error: any) {
      if (isAudioReplacedError(error)) return;

      if (isTranscriptionRetryError(error)) {
        showTranscriptionRetryFailure(error, persistedWorkflow.recordingId);
        return;
      }

      const message =
        error?.message || "Failed to restore audio processing status.";
      console.error("Failed to restore audio processing:", error);
      openWorkflowOverlay(
        "failed",
        100,
        message,
        persistedWorkflow.recordingId,
      );
      setStatus(message);
    }
  };

  const getAudioConstraints = (
    deviceId = selectedMicId,
  ): MediaStreamConstraints => ({
    audio: deviceId
      ? {
          deviceId: { exact: deviceId },
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        }
      : {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
  });

  const syncRecordings = (nextRecordings: SavedRecording[]) => {
    setRecordings(nextRecordings);
    saveRecordingMeta(nextRecordings);
  };

  const patchRecording = (id: string, patch: Partial<SavedRecording>) => {
    setRecordings((prev) => {
      const next = prev.map((recording) =>
        recording.id === id ? { ...recording, ...patch } : recording,
      );
      saveRecordingMeta(next);
      return next;
    });
  };

  const stopTimer = () => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const startTimer = () => {
    stopTimer();

    timerRef.current = window.setInterval(() => {
      recordingSecondsRef.current += 1;
      setRecordingSeconds(recordingSecondsRef.current);
    }, 1000);
  };

  const resetTimer = () => {
    stopTimer();
    recordingSecondsRef.current = 0;
    setRecordingSeconds(0);
  };

  const stopMicLevelMonitor = () => {
    if (volumeTimerRef.current) {
      window.clearInterval(volumeTimerRef.current);
      volumeTimerRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => undefined);
      audioContextRef.current = null;
    }

    setMicLevel(0);
  };

  const stopPreviewStream = () => {
    previewStreamRef.current?.getTracks().forEach((track) => track.stop());
    previewStreamRef.current = null;
    setIsMicPreviewActive(false);
  };

  const stopMicTest = () => {
    if (micTestTimerRef.current) {
      window.clearInterval(micTestTimerRef.current);
      micTestTimerRef.current = null;
    }

    if (micTestAudioContextRef.current) {
      micTestAudioContextRef.current.close().catch(() => undefined);
      micTestAudioContextRef.current = null;
    }

    micTestStreamRef.current?.getTracks().forEach((track) => track.stop());
    micTestStreamRef.current = null;
    setTestingMicId("");
    setMicTestLevel(0);
  };

  const startMicTestLevelMonitor = (stream: MediaStream) => {
    if (micTestTimerRef.current) {
      window.clearInterval(micTestTimerRef.current);
      micTestTimerRef.current = null;
    }

    if (micTestAudioContextRef.current) {
      micTestAudioContextRef.current.close().catch(() => undefined);
      micTestAudioContextRef.current = null;
    }

    const audioContext = new AudioContext();

    if (audioContext.state === "suspended") {
      audioContext.resume().catch(() => undefined);
    }

    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(stream);

    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.78;
    source.connect(analyser);

    micTestAudioContextRef.current = audioContext;

    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    micTestTimerRef.current = window.setInterval(() => {
      if (audioContext.state === "suspended") {
        audioContext.resume().catch(() => undefined);
      }

      analyser.getByteFrequencyData(dataArray);

      const average =
        dataArray.reduce((total, value) => total + value, 0) / dataArray.length;
      const noiseFloor = 5;
      const normalized = average <= noiseFloor ? 0 : average - noiseFloor;
      const nextLevel = Math.min(100, Math.round(normalized * 2.5));

      setMicTestLevel(nextLevel);
    }, 90);
  };

  const startMicLevelMonitor = (stream: MediaStream) => {
    stopMicLevelMonitor();

    const audioContext = new AudioContext();

    if (audioContext.state === "suspended") {
      audioContext.resume().catch(() => undefined);
    }

    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(stream);

    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.78;
    source.connect(analyser);

    audioContextRef.current = audioContext;

    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    volumeTimerRef.current = window.setInterval(() => {
      if (audioContext.state === "suspended") {
        audioContext.resume().catch(() => undefined);
      }

      analyser.getByteFrequencyData(dataArray);

      const average =
        dataArray.reduce((total, value) => total + value, 0) / dataArray.length;
      const noiseFloor = 5;
      const normalized = average <= noiseFloor ? 0 : average - noiseFloor;
      const nextLevel = Math.min(100, Math.round(normalized * 2.5));

      setMicLevel(nextLevel);
    }, 90);
  };

  const startMicPreview = async (deviceId = selectedMicId) => {
    try {
      if (isRecording || !navigator.mediaDevices?.getUserMedia) return;

      stopPreviewStream();
      stopMicLevelMonitor();

      const stream = await navigator.mediaDevices.getUserMedia(
        getAudioConstraints(deviceId),
      );

      previewStreamRef.current = stream;
      setIsMicPreviewActive(true);
      startMicLevelMonitor(stream);
      setStatus("Live mic preview ready");
    } catch (error) {
      console.error("Failed to start live mic preview:", error);
      setIsMicPreviewActive(false);
      setMicLevel(0);
      setStatus("Live mic preview unavailable");
    }
  };

  const loadAudioInputDevices = async () => {
    try {
      if (!navigator.mediaDevices?.enumerateDevices) {
        setStatus("Microphone device selection is not supported");
        return;
      }

      let permissionStream: MediaStream | null = null;

      try {
        permissionStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
      } catch {
        // Browser may still return generic microphone labels.
      } finally {
        permissionStream?.getTracks().forEach((track) => track.stop());
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      const microphones = dedupeAudioInputDevices(
        devices
          .filter((device) => device.kind === "audioinput")
          .map((device, index) => ({
            deviceId: device.deviceId,
            groupId: device.groupId || undefined,
            label: device.label || `Microphone ${index + 1}`,
          })),
      );

      setAudioInputs(microphones);

      const storedMicPreference = getStoredMicPreference();
      const savedMic =
        microphones.find((mic) => mic.deviceId === savedMicId) ||
        microphones.find(
          (mic) => mic.deviceId === storedMicPreference?.deviceId,
        ) ||
        microphones.find(
          (mic) =>
            storedMicPreference?.label &&
            normalizeMicLabel(mic.label) ===
              normalizeMicLabel(storedMicPreference.label),
        );
      const bestMic = getBestAudioInputDevice(microphones);
      const stillHasSelectedMic = microphones.some(
        (mic) => mic.deviceId === selectedMicId,
      );
      const micToUse =
        !selectedMicId || !stillHasSelectedMic
          ? savedMic || bestMic
          : undefined;

      if (micToUse) {
        setSelectedMicId(micToUse.deviceId);
        setSavedMicId(micToUse.deviceId);
        setSelectedMicLabel(micToUse.label);
        saveMicPreference(micToUse);
        window.setTimeout(() => startMicPreview(micToUse.deviceId), 150);
      } else if (selectedMicId) {
        const selectedMic = microphones.find(
          (mic) => mic.deviceId === selectedMicId,
        );

        if (selectedMic) {
          setSelectedMicLabel(selectedMic.label);
        }
      }

      if (microphones.length === 0) {
        setSelectedMicLabel("No microphone found");
        setSavedMicId("");
        setIsMicPreviewActive(false);
        stopMicTest();
      }
    } catch (error) {
      console.error("Failed to load microphones:", error);
      setStatus("Could not load microphones");
    }
  };

  const showMicCoach = (message: string) => {
    setMicCoachNotification(message);

    if (micCoachTimerRef.current) {
      window.clearTimeout(micCoachTimerRef.current);
    }

    micCoachTimerRef.current = window.setTimeout(() => {
      setMicCoachNotification("");
      micCoachTimerRef.current = null;
    }, 3600);
  };

  const handleMicChange = (deviceId: string) => {
    if (isRecording) return;

    const selectedMic = audioInputs.find((mic) => mic.deviceId === deviceId);

    setSelectedMicId(deviceId);
    setSavedMicId(deviceId);
    setSelectedMicLabel(selectedMic?.label || "Selected microphone");

    if (selectedMic) {
      saveMicPreference(selectedMic);
      showMicCoach(`${selectedMic.label} selected and saved.`);
    }

    stopMicTest();

    if (!isRecording) {
      window.setTimeout(() => startMicPreview(deviceId), 80);
    }
  };

  const handleMicTest = async (mic: AudioInputDevice) => {
    if (isRecording || !navigator.mediaDevices?.getUserMedia) return;

    if (testingMicId === mic.deviceId) {
      stopMicTest();
      window.setTimeout(() => startMicPreview(selectedMicId), 120);
      return;
    }

    try {
      stopPreviewStream();
      stopMicLevelMonitor();
      stopMicTest();

      setTestingMicId(mic.deviceId);
      setMicTestLevel(0);
      setStatus(`Testing microphone: ${mic.label}`);

      const stream = await navigator.mediaDevices.getUserMedia(
        getAudioConstraints(mic.deviceId),
      );

      micTestStreamRef.current = stream;
      startMicTestLevelMonitor(stream);
    } catch (error) {
      console.error("Failed to test microphone:", error);
      stopMicTest();
      setStatus("Microphone test failed");
    }
  };

  const triggerSavedFlash = () => {
    setSavedFlash(true);
    window.setTimeout(() => setSavedFlash(false), 2200);
  };

  const addSavedRecordingFromBlob = async (
    blob: Blob,
    options?: { fileName?: string; durationSeconds?: number },
  ): Promise<SavedRecording> => {
    const createdAt = new Date();
    const id = createId();
    const objectUrl = URL.createObjectURL(blob);
    objectUrlsRef.current.push(objectUrl);

    const durationSeconds =
      options?.durationSeconds ??
      Math.round(await getAudioDurationSeconds(blob));
    const extension = getFileExtension(
      blob.type || "audio/webm",
      options?.fileName,
    );
    const cleanProvidedName = options?.fileName?.trim();

    const newRecording: SavedRecording = {
      id,
      name:
        cleanProvidedName ||
        `Recording ${createdAt.toLocaleString()}.${extension}`,
      mimeType: blob.type || "audio/webm",
      size: blob.size,
      durationSeconds,
      durationText: formatTime(durationSeconds),
      createdAt: createdAt.toISOString(),
      objectUrl,
      uploadStatus: "idle",
      uploadProgress: 0,
    };

    await saveBlobToDb(id, blob);

    const updatedRecordings = [newRecording, ...recordings].slice(
      0,
      localRecordingLimit,
    );
    const removedRecordings = [newRecording, ...recordings].slice(
      localRecordingLimit,
    );

    await Promise.all(
      removedRecordings.map(async (recording) => {
        await deleteBlobFromDb(recording.id).catch(() => undefined);
        if (recording.objectUrl) URL.revokeObjectURL(recording.objectUrl);
      }),
    );

    syncRecordings(updatedRecordings);
    setSelectedRecordingId(newRecording.id);
    setIsPreviewOpen(false);
    triggerSavedFlash();
    setStatus("Audio saved locally. Ready to upload.");
    return newRecording;
  };

  function formatDateTimeWithTimezone(isoDate: string) {
    const date = new Date(isoDate);
    const pad = (value: number) => String(value).padStart(2, "0");
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());
    const offsetMinutes = -date.getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? "+" : "-";
    const absoluteOffset = Math.abs(offsetMinutes);
    const offsetHours = pad(Math.floor(absoluteOffset / 60));
    const offsetMins = pad(absoluteOffset % 60);
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}${sign}${offsetHours}:${offsetMins}`;
  }

  const getUploadFileName = (file: File) => {
    const extension = getFileExtension(file.type || "audio/webm", file.name);
    return `treatmentPlanId-${treatmentPlanId}.${extension}`;
  };

  const generateUploadUrls = async (
    recording: SavedRecording,
    file: File,
    signal?: AbortSignal,
  ) => {
    const authHeader =
      axios.defaults.headers.common.Authorization || getDefaultAuthToken();

    const params: Record<string, string | number> = {
      file_name: getUploadFileName(file),
      file_size: file.size,
      duration: Math.round(recording.durationSeconds || 0),
      recorded_at: formatDateTimeWithTimezone(recording.createdAt),
    };

    // Optional according to API docs
    if (treatmentPlanId) {
      params.treatment_plan_id = Number(treatmentPlanId);
    }

    const headers: Record<string, string> = {};

    if (authHeader) {
      headers.Authorization = String(authHeader).startsWith("Bearer ")
        ? String(authHeader)
        : `Bearer ${authHeader}`;
    }

    try {
      const { data } = await axios.post<GenerateUploadResponse>(
        buildApiUrl(apiBaseUrl, `/audio-recording/generate_upload_url`),
        null,
        {
          params,
          withCredentials: false,
          headers,
          signal,
        },
      );

      console.log("Generate upload URL request params:", params);
      console.log("Generate upload URL response:", data);

      if (
        !data.upload_session_id ||
        !data.part_size ||
        !Array.isArray(data.urls)
      ) {
        throw new Error("Invalid generate_upload_url response from backend.");
      }

      return data;
    } catch (error: any) {
      console.error("Generate upload URL axios error:", {
        message: error?.message,
        code: error?.code,
        status: error?.response?.status,
        response: error?.response?.data,
      });

      throw new Error(
        `Generate upload URL failed${
          error?.response?.status ? ` with status ${error.response.status}` : ""
        }${
          error?.response?.data
            ? `: ${
                typeof error.response.data === "string"
                  ? error.response.data
                  : JSON.stringify(error.response.data)
              }`
            : error?.message
              ? `: ${error.message}`
              : ""
        }`,
      );
    }
  };

  const generateCustomUploadUrls = async (
    uploadSessionId: string,
    partNumbers: number[],
    signal?: AbortSignal,
  ): Promise<GenerateUploadResponse> => {
    const token = axios.defaults.headers.common.Authorization;

    try {
      const { data } = await axios.post<GenerateUploadResponse>(
        buildApiUrl(
          apiBaseUrl,
          `/audio-recording/generate_custom_upload_url/${uploadSessionId}`,
        ),
        {
          part_numbers: partNumbers,
        },
        {
          withCredentials: true,
          headers: {
            Authorization: String(token || ""),
            "Content-Type": "application/json",
          },
          signal,
        },
      );

      if (
        !data.upload_session_id ||
        !data.part_size ||
        !Array.isArray(data.urls)
      ) {
        throw new Error(
          "Invalid generate_custom_upload_url response from backend.",
        );
      }

      return data;
    } catch (error: any) {
      const status = error?.response?.status;
      const responseData = error?.response?.data;

      throw new Error(
        `Generate custom upload URL failed${
          status ? ` with status ${status}` : ""
        }${
          responseData
            ? `: ${
                typeof responseData === "string"
                  ? responseData
                  : JSON.stringify(responseData)
              }`
            : ""
        }`,
      );
    }
  };

  const uploadOnePart = async (
    url: string,
    partBlob: Blob,
    partNumber: number,
    mimeType: string,
    signal?: AbortSignal,
  ): Promise<UploadPartResult> => {
    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": mimeType || "application/octet-stream",
      },
      body: partBlob,
      signal,
    });

    if (!response.ok) {
      throw new Error(
        `Part ${partNumber} upload failed with status ${response.status}`,
      );
    }

    const etag =
      response.headers.get("ETag") || response.headers.get("etag") || "";

    return {
      part_number: partNumber,
      etag,
      size: partBlob.size,
    };
  };

  const uploadAudioPartsToS3 = async (
    file: File | Blob,
    generateResponse: GenerateUploadResponse,
    mimeType: string,
    onProgress?: (progress: number, uploadedParts: UploadPartResult[]) => void,
    options?: { signal?: AbortSignal; shouldCancel?: () => boolean },
  ): Promise<UploadPartResult[]> => {
    const uploadedParts: UploadPartResult[] = [];
    const failedPartNumbers: number[] = [];

    const assertUploadStillCurrent = () => {
      if (options?.signal?.aborted || options?.shouldCancel?.()) {
        throw createAudioReplacedError();
      }
    };

    const partSize = generateResponse.part_size;
    const totalParts = generateResponse.urls.length;

    const uploadPartFromUrlItem = async (
      urlItem: PresignedUrlItem,
      index: number,
    ) => {
      const uploadUrl = getUploadUrl(urlItem);
      const partNumber = getPartNumber(urlItem, index);

      if (!uploadUrl) {
        throw new Error(`Missing pre-signed URL for part ${partNumber}`);
      }

      const start = (partNumber - 1) * partSize;
      const end = Math.min(start + partSize, file.size);

      const partBlob = file.slice(start, end, mimeType);

      assertUploadStillCurrent();
      return uploadOnePart(
        uploadUrl,
        partBlob,
        partNumber,
        mimeType,
        options?.signal,
      );
    };

    for (let index = 0; index < generateResponse.urls.length; index += 1) {
      assertUploadStillCurrent();
      const urlItem = generateResponse.urls[index];
      const partNumber = getPartNumber(urlItem, index);

      try {
        const uploadedPart = await uploadPartFromUrlItem(urlItem, index);
        uploadedParts.push(uploadedPart);
      } catch (error) {
        console.error(`Part ${partNumber} failed on first upload:`, error);
        failedPartNumbers.push(partNumber);
      }

      assertUploadStillCurrent();
      const progress = Math.round((uploadedParts.length / totalParts) * 100);
      onProgress?.(progress, [...uploadedParts]);
    }

    if (failedPartNumbers.length === 0) {
      return uploadedParts.sort((a, b) => a.part_number - b.part_number);
    }

    const customResponse = await generateCustomUploadUrls(
      generateResponse.upload_session_id,
      failedPartNumbers,
      options?.signal,
    );

    for (let index = 0; index < customResponse.urls.length; index += 1) {
      assertUploadStillCurrent();
      const urlItem = customResponse.urls[index];
      const partNumber = getPartNumber(urlItem, index);

      try {
        const uploadedPart = await uploadPartFromUrlItem(urlItem, index);

        const existingIndex = uploadedParts.findIndex(
          (part) => part.part_number === uploadedPart.part_number,
        );

        if (existingIndex >= 0) {
          uploadedParts[existingIndex] = uploadedPart;
        } else {
          uploadedParts.push(uploadedPart);
        }
      } catch (error) {
        console.error(
          `Part ${partNumber} failed after custom URL retry:`,
          error,
        );
        throw new Error(`Part ${partNumber} failed after custom URL retry.`);
      }

      assertUploadStillCurrent();
      const progress = Math.round((uploadedParts.length / totalParts) * 100);
      onProgress?.(progress, [...uploadedParts]);
    }

    assertUploadStillCurrent();
    return uploadedParts.sort((a, b) => a.part_number - b.part_number);
  };

  const uploadRecordingWithPresignedUrls = async (
    recording: SavedRecording,
    options?: { workflowToken?: number },
  ) => {
    if (!canUploadAndTranscribe) {
      setStatus(uploadDisabledMessage);
      return;
    }

    const workflowToken = options?.workflowToken ?? beginFreshAudioWorkflow();
    const uploadAbortController =
      activeAudioUploadAbortRef.current || new AbortController();
    activeAudioUploadAbortRef.current = uploadAbortController;

    const assertCurrentWorkflow = () => {
      if (
        !isCurrentAudioWorkflow(workflowToken) ||
        uploadAbortController.signal.aborted
      ) {
        throw createAudioReplacedError();
      }
    };

    let activeUploadJob: PendingUploadJob | null = null;
    let lockedUploadSessionId: string | null = null;

    try {
      assertCurrentWorkflow();
      setStatus("Requesting presigned upload URLs...");
      openWorkflowOverlay("generatingLink", 0);

      patchRecording(recording.id, {
        uploadStatus: "uploading",
        uploadProgress: 0,
        errorMessage: undefined,
      });

      const audioFile = await getRecordingFile(recording);

      assertCurrentWorkflow();
      const generateResponse = await generateUploadUrls(
        recording,
        audioFile,
        uploadAbortController.signal,
      );
      assertCurrentWorkflow();

      activeUploadJob = {
        recordingId: recording.id,
        consentBundleId,
        treatmentPlanId,
        uploadSessionId: generateResponse.upload_session_id,
        fileName: getUploadFileName(audioFile),
        mimeType: audioFile.type || recording.mimeType,
        size: audioFile.size,
        durationSeconds: recording.durationSeconds,
        recordedAt: recording.createdAt,
        partSize: generateResponse.part_size,
        urls: generateResponse.urls,
        attempts: 1,
        maxAttempts: maxUploadAttempts,
        createdAtMs: Date.now(),
        expiresAtMs: Date.now() + retryWindowMinutes * 60 * 1000,
        uploadedParts: [],
        status: "uploading",
      };

      if (inFlightUploadSessions.has(activeUploadJob.uploadSessionId)) {
        throw new Error("This upload session is already running.");
      }

      lockedUploadSessionId = activeUploadJob.uploadSessionId;
      inFlightUploadSessions.add(lockedUploadSessionId);

      upsertPendingUploadJob(activeUploadJob);
      activatePersistedAudioWorkflow(
        activeUploadJob,
        "uploading",
        "Uploading audio",
      );

      setStatus("Upload started.");
      openWorkflowOverlay("uploading", 0);

      const uploadedParts = await uploadAudioPartsToS3(
        audioFile,
        generateResponse,
        audioFile.type || recording.mimeType || "application/octet-stream",
        (progress, currentUploadedParts) => {
          if (!isCurrentAudioWorkflow(workflowToken)) return;

          patchRecording(recording.id, {
            uploadProgress: progress,
          });

          if (activeUploadJob) {
            activeUploadJob = {
              ...activeUploadJob,
              uploadedParts: currentUploadedParts,
              status: "uploading",
            };
            upsertPendingUploadJob(activeUploadJob);
          }

          openWorkflowOverlay("uploading", progress);
        },
        {
          signal: uploadAbortController.signal,
          shouldCancel: () => !isCurrentAudioWorkflow(workflowToken),
        },
      );

      assertCurrentWorkflow();
      setStatus("Completing upload session...");
      openWorkflowOverlay("uploading", 100);

      const uploadJob: PendingUploadJob = {
        ...(activeUploadJob as PendingUploadJob),
        uploadedParts,
        status: "finalizing",
      };

      activeUploadJob = uploadJob;
      upsertPendingUploadJob(uploadJob);

      const completeResponse = await completeUploadOnce(
        uploadJob,
        uploadedParts,
        uploadAbortController.signal,
      );

      assertCurrentWorkflow();
      removePendingUploadJob(uploadJob.uploadSessionId);
      activeUploadJob = { ...uploadJob, status: "completed" };

      patchRecording(recording.id, {
        uploadStatus: "uploaded",
        uploadProgress: 100,
        remoteKey: completeResponse.key || completeResponse.Key,
        remoteLocation: completeResponse.location || completeResponse.Location,
        errorMessage: undefined,
      });

      onUploadComplete?.({
        recording: {
          ...recording,
          uploadStatus: "uploaded",
          uploadProgress: 100,
          remoteKey: completeResponse.key || completeResponse.Key,
          remoteLocation:
            completeResponse.location || completeResponse.Location,
        },
        response: completeResponse,
      });

      setStatus("Audio upload completed. Starting transcription...");
      await runTranscriptionAndPatientNoteFlow(uploadJob, completeResponse, {
        workflowToken,
        signal: uploadAbortController.signal,
      });
      if (isCurrentAudioWorkflow(workflowToken)) {
        setStatus("Patient note generated. Review and save the note.");
      }
    } catch (error: any) {
      if (
        isAudioReplacedError(error) ||
        !isCurrentAudioWorkflow(workflowToken)
      ) {
        if (activeUploadJob)
          removePendingUploadJob(activeUploadJob.uploadSessionId);
        return;
      }

      if (isTranscriptionRetryError(error)) {
        showTranscriptionRetryFailure(
          error,
          activeUploadJob?.recordingId || recording.id,
        );
        return;
      }

      console.error(error);

      const message = error?.message || "Audio upload failed.";

      if (activeUploadJob?.status === "finalizing") {
        removePendingUploadJob(activeUploadJob.uploadSessionId);
      } else if (activeUploadJob && activeUploadJob.status !== "completed") {
        upsertPendingUploadJob({
          ...activeUploadJob,
          status: "failed",
          lastAttemptAtMs: Date.now(),
          lastError: message,
        });
      }

      patchRecording(recording.id, {
        uploadStatus:
          activeUploadJob?.status === "completed" ? "uploaded" : "failed",
        errorMessage: message,
      });

      openWorkflowOverlay("failed", 100, message, recording.id);
      setStatus(message);
      onUploadFailed?.({ recording, error });
    } finally {
      if (lockedUploadSessionId) {
        inFlightUploadSessions.delete(lockedUploadSessionId);
      }
    }
  };

  const completeUpload = async (
    job: PendingUploadJob,
    parts: UploadPartResult[],
    signal?: AbortSignal,
  ): Promise<CompleteUploadResponse> => {
    const token = axios.defaults.headers.common.Authorization;

    const expectedPartNumbers = job.urls.map((item, index) =>
      getPartNumber(item, index),
    );
    const uniqueParts = new Map(
      parts.map((part) => [part.part_number, part] as const),
    );
    const missingPartNumbers = expectedPartNumbers.filter((partNumber) => {
      const part = uniqueParts.get(partNumber);
      return !part || !String(part.etag || "").trim();
    });

    if (
      uniqueParts.size !== expectedPartNumbers.length ||
      missingPartNumbers.length > 0
    ) {
      throw createUploadCompletionError(
        `Upload completion was blocked because not all S3 parts succeeded. Missing parts: ${
          missingPartNumbers.join(", ") || "unknown"
        }.`,
      );
    }

    const payload = {
      parts: [...uniqueParts.values()]
        .sort((a, b) => a.part_number - b.part_number)
        .map((part) => ({
          PartNumber: part.part_number,
          ETag: part.etag,
        })),
    };

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers.Authorization = String(token);
    }

    try {
      const { data } = await axios.post<CompleteUploadResponse>(
        buildApiUrl(
          apiBaseUrl,
          `/audio-recording/upload-complete/${job.uploadSessionId}`,
        ),
        payload,
        {
          withCredentials: false,
          headers,
          signal,
        },
      );

      console.log("Upload complete response:", data);
      return data;
    } catch (error: any) {
      if (signal?.aborted || isAudioReplacedError(error)) {
        throw error;
      }

      console.error("Upload complete axios error:", {
        message: error?.message,
        code: error?.code,
        status: error?.response?.status,
        response: error?.response?.data,
        request: error?.request,
      });

      throw createUploadCompletionError(
        `Upload complete failed${
          error?.response?.status ? ` with status ${error.response.status}` : ""
        }${
          error?.response?.data
            ? `: ${
                typeof error.response.data === "string"
                  ? error.response.data
                  : JSON.stringify(error.response.data)
              }`
            : error?.message
              ? `: ${error.message}`
              : ""
        }`,
      );
    }
  };

  const completeUploadOnce = async (
    job: PendingUploadJob,
    parts: UploadPartResult[],
    signal?: AbortSignal,
  ): Promise<CompleteUploadResponse> => {
    const completeResponse = await completeUpload(job, parts, signal);
    const completeStatus = normalizeWorkflowStatus(
      completeResponse?.status,
    ).replace(/[\s-]+/g, "_");

    if (normalizeProcessingState(completeStatus) === "failed") {
      throw createUploadCompletionError(
        `Upload completion returned failed status: ${
          completeResponse?.status || "failed"
        }.`,
      );
    }

    return completeResponse;
  };

  const runUploadJob = async (job: PendingUploadJob) => {
    const now = Date.now();
    const normalizedJob: PendingUploadJob = {
      ...job,
      treatmentPlanId: job.treatmentPlanId || treatmentPlanId,
    };
    job = normalizedJob;

    activatePersistedAudioWorkflow(job, "uploading", "Resuming audio upload");

    if (now > job.expiresAtMs) {
      const expiredJob: PendingUploadJob = {
        ...job,
        status: "expired",
        lastError: "Upload session expired after retry window.",
      };
      upsertPendingUploadJob(expiredJob);
      patchRecording(job.recordingId, {
        uploadStatus: "expired",
        errorMessage: expiredJob.lastError,
      });
      return;
    }

    if (job.attempts >= job.maxAttempts) {
      const failedJob: PendingUploadJob = {
        ...job,
        status: "failed",
        lastError: "Maximum upload retry attempts reached.",
      };
      upsertPendingUploadJob(failedJob);
      patchRecording(job.recordingId, {
        uploadStatus: "failed",
        errorMessage: failedJob.lastError,
      });
      return;
    }

    const blob = await getBlobFromDb(job.recordingId);
    if (!blob) {
      throw new Error("Local audio blob not found for retry upload.");
    }

    let workingJob: PendingUploadJob = {
      ...job,
      attempts: job.attempts + 1,
      lastAttemptAtMs: now,
      status: "uploading",
      lastError: undefined,
    };

    upsertPendingUploadJob(workingJob);
    patchRecording(job.recordingId, {
      uploadStatus: "uploading",
      uploadSessionId: job.uploadSessionId,
      errorMessage: undefined,
    });

    const uploadedParts = [...workingJob.uploadedParts];
    const uploadedPartNumbers = new Set(
      uploadedParts.map((part) => part.part_number),
    );

    for (let index = 0; index < workingJob.urls.length; index += 1) {
      const urlItem = workingJob.urls[index];
      const uploadUrl = getUploadUrl(urlItem);
      const partNumber = getPartNumber(urlItem, index);

      if (!uploadUrl) {
        throw new Error(`Missing presigned URL for part ${partNumber}.`);
      }

      if (uploadedPartNumbers.has(partNumber)) {
        continue;
      }

      const start = index * workingJob.partSize;
      const end = Math.min(start + workingJob.partSize, blob.size);
      const partBlob = blob.slice(start, end, workingJob.mimeType);

      const result = await uploadOnePart(
        uploadUrl,
        partBlob,
        partNumber,
        workingJob.mimeType,
      );

      uploadedParts.push(result);
      uploadedPartNumbers.add(partNumber);

      const progress = Math.round(
        (uploadedParts.length / workingJob.urls.length) * 100,
      );
      patchRecording(job.recordingId, { uploadProgress: progress });
      openWorkflowOverlay(
        "uploading",
        progress,
        undefined,
        undefined,
        `Resuming audio upload ${progress}%`,
      );

      workingJob = {
        ...workingJob,
        uploadedParts,
      };
      upsertPendingUploadJob(workingJob);
    }

    workingJob = {
      ...workingJob,
      uploadedParts,
      status: "finalizing",
    };
    upsertPendingUploadJob(workingJob);
    openWorkflowOverlay(
      "uploading",
      100,
      undefined,
      undefined,
      "Finalizing audio upload",
    );

    const completeResponse = await completeUploadOnce(
      workingJob,
      uploadedParts,
    );

    removePendingUploadJob(workingJob.uploadSessionId);
    patchRecording(job.recordingId, {
      uploadStatus: "uploaded",
      uploadProgress: 100,
      remoteKey: completeResponse.key || completeResponse.Key,
      remoteLocation: completeResponse.location || completeResponse.Location,
      errorMessage: undefined,
    });

    const uploadedRecording = recordings.find(
      (item) => item.id === job.recordingId,
    );
    if (uploadedRecording) {
      onUploadComplete?.({
        recording: {
          ...uploadedRecording,
          uploadStatus: "uploaded",
          uploadProgress: 100,
          remoteKey: completeResponse.key || completeResponse.Key,
          remoteLocation:
            completeResponse.location || completeResponse.Location,
        },
        response: completeResponse,
      });
    }

    setStatus("Audio upload completed. Starting transcription...");
    try {
      await runTranscriptionAndPatientNoteFlow(workingJob, completeResponse);
      setStatus("Patient note generated. Review and save the note.");
    } catch (processingError: any) {
      if (isTranscriptionRetryError(processingError)) {
        showTranscriptionRetryFailure(processingError, job.recordingId);
        return;
      }

      const message =
        processingError?.message || "Audio processing status polling failed.";
      console.error(
        "Audio processing failed after upload completed:",
        processingError,
      );
      openWorkflowOverlay("failed", 100, message, job.recordingId);
      setStatus(message);
    }
  };

  // depricated
  const queueUploadRetry = (
    recording: SavedRecording,
    file: File,
    generateResponse: GenerateUploadResponse,
  ) => {
    const now = Date.now();
    const job: PendingUploadJob = {
      recordingId: recording.id,
      consentBundleId,
      treatmentPlanId,
      uploadSessionId: generateResponse.upload_session_id,
      fileName: file.name,
      mimeType: file.type || recording.mimeType,
      size: file.size,
      durationSeconds: recording.durationSeconds,
      recordedAt: recording.createdAt,
      partSize: generateResponse.part_size,
      urls: generateResponse.urls,
      attempts: 0,
      maxAttempts: maxUploadAttempts,
      createdAtMs: now,
      expiresAtMs: now + retryWindowMinutes * 60 * 1000,
      uploadedParts: [],
      status: "queued",
    };

    upsertPendingUploadJob(job);
    patchRecording(recording.id, {
      uploadStatus: "queued",
      uploadSessionId: job.uploadSessionId,
      uploadProgress: 0,
    });

    return job;
  };

  const retryPendingUploads = async () => {
    if (isRetryingPendingUploadsRef.current) return;
    isRetryingPendingUploadsRef.current = true;

    try {
      const jobs = getPendingUploadJobs().filter(
        (job) =>
          String(job.treatmentPlanId ?? "").trim() ===
            String(treatmentPlanId ?? "").trim() &&
          String(job.consentBundleId ?? "").trim() ===
            String(consentBundleId ?? "").trim() &&
          (job.status === "queued" ||
            job.status === "failed" ||
            job.status === "uploading") &&
          Date.now() <= job.expiresAtMs,
      );

      for (const job of jobs) {
        try {
          if (job.attempts >= job.maxAttempts) {
            const errorMessage = "Maximum upload retry attempts reached.";

            const failedJob: PendingUploadJob = {
              ...job,
              status: "failed",
              lastAttemptAtMs: Date.now(),
              lastError: errorMessage,
            };

            upsertPendingUploadJob(failedJob);

            patchRecording(job.recordingId, {
              uploadStatus: "failed",
              errorMessage,
            });

            // This job is terminal. Clear it locally.
            removePendingUploadJob(failedJob.uploadSessionId);

            continue;
          }

          if (inFlightUploadSessions.has(job.uploadSessionId)) {
            continue;
          }

          inFlightUploadSessions.add(job.uploadSessionId);
          try {
            await runUploadJob(job);
          } finally {
            inFlightUploadSessions.delete(job.uploadSessionId);
          }
        } catch (error: any) {
          console.error("Pending upload retry failed:", error);

          if (error?.name === "UploadCompletionError") {
            removePendingUploadJob(job.uploadSessionId);
            patchRecording(job.recordingId, {
              uploadStatus: "failed",
              errorMessage:
                error?.message ||
                "Upload completion failed and was not retried.",
            });
            continue;
          }

          const nextAttempts = Math.min(job.attempts + 1, job.maxAttempts);
          const errorMessage = error?.message || "Pending upload retry failed.";

          const nextJob: PendingUploadJob = {
            ...job,
            attempts: nextAttempts,
            status: "failed",
            lastAttemptAtMs: Date.now(),
            lastError: errorMessage,
          };

          upsertPendingUploadJob(nextJob);

          const reachedMaxAttempts = nextAttempts >= nextJob.maxAttempts;

          patchRecording(job.recordingId, {
            uploadStatus: reachedMaxAttempts ? "failed" : "queued",
            errorMessage,
          });

          if (reachedMaxAttempts) {
            // The upload reached its retry limit. Clear the local job only.
            removePendingUploadJob(nextJob.uploadSessionId);
          }
        }
      }
    } finally {
      isRetryingPendingUploadsRef.current = false;
    }
  };

  const hasPatientNoteEditorReachedBottom = () => {
    const instance = patientNoteCkEditorRef.current;
    const threshold = 8;

    const isAtScrollableBottom = (
      scrollTop: number,
      clientHeight: number,
      scrollHeight: number,
    ) => {
      if (!scrollHeight || !clientHeight) return false;

      // Important: do not auto-approve short/non-scrollable content.
      // The clinician must make a real scroll interaction before copy/save is enabled.
      if (scrollHeight <= clientHeight + threshold) return false;

      return scrollTop + clientHeight >= scrollHeight - threshold;
    };

    const iframeDocument = instance?.document?.$ as Document | undefined;

    if (iframeDocument) {
      const iframeWindow = iframeDocument.defaultView;
      const documentElement = iframeDocument.documentElement;
      const body = iframeDocument.body;

      const scrollTop = Math.max(
        iframeWindow?.scrollY || 0,
        documentElement?.scrollTop || 0,
        body?.scrollTop || 0,
      );
      const clientHeight = Math.max(
        iframeWindow?.innerHeight || 0,
        documentElement?.clientHeight || 0,
        body?.clientHeight || 0,
      );
      const scrollHeight = Math.max(
        documentElement?.scrollHeight || 0,
        body?.scrollHeight || 0,
      );

      return isAtScrollableBottom(scrollTop, clientHeight, scrollHeight);
    }

    const editor = patientNoteEditorRef.current;

    if (!editor) return false;

    return isAtScrollableBottom(
      editor.scrollTop,
      editor.clientHeight,
      editor.scrollHeight,
    );
  };

  const markPatientNoteReviewInteraction = () => {
    patientNoteReviewUserInteractedRef.current = true;
  };

  const handlePatientNoteEditorScroll = () => {
    if (isPatientNoteReviewedToBottom) return;

    // Ignore CKEditor/browser scroll events caused by initial rendering or setData().
    // Only enable actions after the clinician physically scrolls/interacts.
    if (!patientNoteReviewUserInteractedRef.current) return;

    if (hasPatientNoteEditorReachedBottom()) {
      setIsPatientNoteReviewedToBottom(true);
      setPatientNoteEditor((prev) => ({
        ...prev,
        errorMessage: undefined,
      }));
    }
  };

  const handlePatientNoteEditorUserInteraction = () => {
    markPatientNoteReviewInteraction();
  };

  const triggerPatientNoteCopiedFlash = () => {
    setIsPatientNoteCopied(true);

    if (patientNoteCopyTimerRef.current) {
      window.clearTimeout(patientNoteCopyTimerRef.current);
    }

    patientNoteCopyTimerRef.current = window.setTimeout(() => {
      setIsPatientNoteCopied(false);
      patientNoteCopyTimerRef.current = null;
    }, 2200);
  };

  const getPatientNoteEditorText = () => {
    const editorHtml = patientNoteCkEditorRef.current?.getData?.();

    return normalizeEditedPatientNoteText(
      editorHtml
        ? patientNoteHtmlToMarkdownText(editorHtml)
        : patientNoteEditor.patientNoteText,
    );
  };

  const syncPatientNoteFromCKEditor = () => {
    const editorHtml = patientNoteCkEditorRef.current?.getData?.() || "";
    const nextValue = patientNoteHtmlToMarkdownText(editorHtml);

    setIsPatientNoteCopied(false);
    setPatientNoteEditor((prev) => ({
      ...prev,
      patientNoteText: nextValue,
      patientNoteHtml: editorHtml,
      errorMessage: undefined,
    }));
  };

  const handleCopyPatientNote = async () => {
    if (!isPatientNoteReviewedToBottom) {
      setPatientNoteEditor((prev) => ({
        ...prev,
        errorMessage:
          "Please scroll to the bottom of the patient note before copying.",
      }));
      return;
    }

    const patientNoteHtml =
      patientNoteCkEditorRef.current?.getData?.() ||
      patientNoteEditor.patientNoteHtml ||
      formatPatientNoteHtmlForEditor(patientNoteEditor.patientNoteText);
    const patientNotePlainText = patientNoteHtmlToPlainText(patientNoteHtml);

    if (!patientNotePlainText) {
      setPatientNoteEditor((prev) => ({
        ...prev,
        errorMessage: "Patient note is empty. Add text before copying.",
      }));
      return;
    }

    try {
      await copyRichTextToClipboard(patientNoteHtml, patientNotePlainText);
      triggerPatientNoteCopiedFlash();
      setStatus("Patient note copied to clipboard.");
      setPatientNoteEditor((prev) => ({
        ...prev,
        errorMessage: undefined,
      }));
    } catch (error: any) {
      setPatientNoteEditor((prev) => ({
        ...prev,
        errorMessage:
          error?.message ||
          "Could not copy patient note. Please select the text and copy it manually.",
      }));
    }
  };

  const handleClosePatientNoteEditor = () => {
    setIsPatientNoteCopied(false);
    patientNoteReviewUserInteractedRef.current = false;
    setIsPatientNoteReviewedToBottom(false);
    setPatientNoteEditor((prev) => ({
      ...prev,
      isOpen: false,
      isSaving: false,
      errorMessage: undefined,
    }));
    onRequestClose?.();
  };

  const handleSavePatientNote = async () => {
    if (!isPatientNoteReviewedToBottom) {
      setPatientNoteEditor((prev) => ({
        ...prev,
        errorMessage:
          "Please scroll to the bottom of the patient note before saving.",
      }));
      return;
    }

    const editedPatientNote = parseEditedPatientNote(
      getPatientNoteEditorText(),
    );
    let uploadSessionId = patientNoteEditor.uploadSessionId;

    setPatientNoteEditor((prev) => ({
      ...prev,
      isSaving: true,
      errorMessage: undefined,
    }));

    try {
      if (!uploadSessionId) {
        const latestAudioRecordData = await getAudioRecordData();

        if (
          !doesAudioRecordMatchContext(
            latestAudioRecordData,
            treatmentPlanId,
            consentBundleId,
            matchConsentBundleId,
          )
        ) {
          throw new Error(
            "The audio record does not belong to this treatment plan and consent bundle.",
          );
        }

        const latestPatientNoteStatus = normalizeWorkflowStatus(
          getFirstAudioRecordValue(latestAudioRecordData, [
            "patient_note_status",
            "patient_note_generation_status",
            "patientNoteStatus",
            "patient_note.status",
            "patientNote.status",
          ]),
        ).replace(/[\s-]+/g, "_");

        if (latestPatientNoteStatus === "edited") {
          clearActivePersistedAudioWorkflow();
          setStatus("Patient note has already been reviewed and edited.");
          setPatientNoteEditor((prev) => ({
            ...prev,
            isOpen: false,
            isSaving: false,
            errorMessage: undefined,
          }));
          onRequestClose?.();
          return;
        }

        if (latestPatientNoteStatus !== "generated") {
          throw new Error(
            `Patient note is not ready for editing. Current status: ${
              latestPatientNoteStatus || "unknown"
            }.`,
          );
        }

        uploadSessionId = getUploadSessionIdFromAudioRecordData(
          latestAudioRecordData,
        );

        if (!uploadSessionId) {
          throw new Error(
            "The get_audio_record_data response did not include upload_session_id.",
          );
        }

        setPatientNoteEditor((prev) => ({
          ...prev,
          uploadSessionId,
        }));
      }

      const editPatientNoteResponse = await axios.post(
        buildApiUrl(apiBaseUrl, EDIT_PATIENT_NOTE_PATH),
        {
          treatment_plan_id: Number(treatmentPlanId),
          upload_session_id: uploadSessionId,
          edited_patient_note: editedPatientNote,
        },
        {
          withCredentials: false,
          headers: getWorkflowRequestHeaders(true),
        },
      );

      const refreshedAudioRecordData =
        editPatientNoteResponse.status === 200
          ? await getAudioRecordData()
          : null;

      onPatientNoteSaved?.({
        uploadSessionId,
        editedPatientNote,
        response: editPatientNoteResponse.data,
        audioRecordData: refreshedAudioRecordData,
      });

      clearActivePersistedAudioWorkflow();
      setStatus("Patient note saved successfully.");
      setIsPatientNoteReviewedToBottom(false);
      setPatientNoteEditor((prev) => ({
        ...prev,
        isOpen: false,
        isSaving: false,
        errorMessage: undefined,
      }));
      onRequestClose?.();
    } catch (error: any) {
      console.error("Save patient note failed:", error);

      setPatientNoteEditor((prev) => ({
        ...prev,
        isSaving: false,
        errorMessage:
          error?.response?.data?.detail ||
          error?.response?.data?.error ||
          error?.message ||
          "Patient note save failed.",
      }));
    }
  };

  const handleUploadSelected = async () => {
    if (!canUploadAndTranscribe) {
      setStatus(uploadDisabledMessage);
      return;
    }

    if (!selectedRecording) {
      setStatus("Select a saved recording first.");
      return;
    }

    await uploadRecordingWithPresignedUrls(selectedRecording);
  };

  const handleReuploadFromFailure = async () => {
    if (!canUploadAndTranscribe) {
      setStatus(uploadDisabledMessage);
      return;
    }

    const recordingToRetry = workflowFailedRecording;

    if (!recordingToRetry) {
      setStatus(
        "Recording not found. Please record or upload the audio again.",
      );
      closeWorkflowOverlay();
      return;
    }

    setSelectedRecordingId(recordingToRetry.id);
    await uploadRecordingWithPresignedUrls(recordingToRetry);
  };

  const handleRetryTranscription = async () => {
    const persistedWorkflow =
      activePersistedWorkflowRef.current ||
      getActiveAudioWorkflowForTreatmentPlan(treatmentPlanId, consentBundleId);
    const uploadSessionId = String(
      workflowOverlay.failedUploadSessionId ||
        persistedWorkflow?.uploadSessionId ||
        "",
    ).trim();

    if (!uploadSessionId) {
      setStatus(
        "The upload session ID is missing. Please upload the audio again.",
      );
      return;
    }

    const workflowToken = beginFreshAudioWorkflow();
    const signal = activeAudioUploadAbortRef.current?.signal;
    const retryJob: PendingUploadJob = {
      recordingId:
        workflowOverlay.failedRecordingId ||
        persistedWorkflow?.recordingId ||
        "",
      consentBundleId: persistedWorkflow?.consentBundleId || consentBundleId,
      treatmentPlanId:
        persistedWorkflow?.treatmentPlanId || treatmentPlanId,
      uploadSessionId,
      fileName: "",
      mimeType: "audio/webm",
      size: 0,
      durationSeconds: 0,
      recordedAt: new Date().toISOString(),
      partSize: 0,
      urls: [],
      attempts: 0,
      maxAttempts: maxUploadAttempts,
      createdAtMs: Date.now(),
      expiresAtMs: Date.now() + retryWindowMinutes * 60 * 1000,
      uploadedParts: [],
      status: "completed",
    };

    activatePersistedAudioWorkflow(
      retryJob,
      "transcribing",
      "Restarting transcription",
    );
    openWorkflowOverlay(
      "transcribing",
      undefined,
      undefined,
      undefined,
      "Restarting transcription",
    );
    setStatus("Restarting transcription and patient-note generation...");

    try {
      await generateTranscriptAndPatientNote(uploadSessionId, signal);

      await runTranscriptionAndPatientNoteFlow(
        retryJob,
        {
          upload_session_id: uploadSessionId,
          status: "processing",
        },
        {
          workflowToken,
          signal,
          resume: true,
          pollImmediately: true,
        },
      );
    } catch (error: any) {
      if (isAudioReplacedError(error)) return;

      const retryError = isTranscriptionRetryError(error)
        ? error
        : createTranscriptionRetryError(
            error?.response?.data?.detail ||
              error?.response?.data?.error ||
              error?.message ||
              "Failed to restart transcription. Please try again.",
            uploadSessionId,
          );

      showTranscriptionRetryFailure(retryError, retryJob.recordingId);
    }
  };

  const closeConfirmModal = () => setConfirmModal(null);

  const askToCloseAudioModal = () => {
    if (unsafeCloseChoicePendingRef.current) return;

    if (!shouldConfirmUnsafeExit) {
      onRequestClose?.();
      return;
    }

    unsafeCloseChoicePendingRef.current = true;

    const recorder = mediaRecorderRef.current;
    const shouldResumeRecording =
      isRecording && recorder?.state === "recording";

    resumeRecordingAfterUnsafeCloseCancelRef.current = shouldResumeRecording;

    if (shouldResumeRecording) {
      recorder.pause();
      setStatus("Recording paused while waiting for your choice.");
    }

    setConfirmModal({
      action: "unsafeClose",
      title: unsafeExitTitle,
      message: unsafeExitMessage,
      confirmLabel: "Close anyway",
      cancelLabel: isRecording ? "Continue recording" : "Stay here",
      danger: true,
    });
  };

  const askToPauseRecording = () => {
    setConfirmModal({
      action: "pause",
      title: "Pause recording?",
      message:
        "The recording will pause now. You can resume it later without losing the current audio.",
      confirmLabel: "Pause for now",
      cancelLabel: "No, keep recording",
    });
  };

  const askToResumeRecording = () => {
    setConfirmModal({
      action: "resume",
      title: "Resume recording?",
      message: "The recorder will continue from where it was paused.",
      confirmLabel: "Yes, resume",
      cancelLabel: "No, keep paused",
    });
  };

  const askToStopRecording = () => {
    setConfirmModal({
      action: "stop",
      title: "Stop and save recording?",
      message:
        "This will save one complete recording locally. You can then upload it through presigned URLs.",
      confirmLabel: "Yes, stop and save",
      cancelLabel: "No, continue recording",
      danger: true,
    });
  };

  const askToStartTranscriptionUpload = () => {
    if (!canUploadAndTranscribe) {
      setStatus(uploadDisabledMessage);
      return;
    }

    const recorder = mediaRecorderRef.current;

    if (
      !recorder ||
      (recorder.state !== "recording" && recorder.state !== "paused")
    ) {
      setStatus("No active recording found.");
      return;
    }

    shouldUploadAfterStopRef.current = false;

    if (recorder.state === "recording") {
      recorder.pause();
    }

    setStatus("Recording paused. Confirm before upload starts.");
    setConfirmModal({
      action: "startTranscribe",
      title: "Ready to transcribe?",
      message: "",
      confirmLabel: "Start transcription",
      cancelLabel: "No, resume recording",
      variant: "process",
      details: [],
    });
  };

  const askToDeleteRecording = (id: string) => {
    setConfirmModal({
      action: "delete",
      title: "Delete this recording?",
      message:
        "This saved recording will be removed from local storage. This action cannot be undone.",
      confirmLabel: "Yes, delete",
      cancelLabel: "No, keep it",
      danger: true,
      recordingId: id,
    });
  };

  const askToClearAllRecordings = () => {
    setConfirmModal({
      action: "clearAll",
      title: "Clear all saved recordings?",
      message:
        "All saved local recordings and pending upload sessions will be deleted. This action cannot be undone.",
      confirmLabel: "Yes, clear all",
      cancelLabel: "No, keep recordings",
      danger: true,
    });
  };

  const startRecording = async () => {
    try {
      let deviceIdForRecording = selectedMicId;
      const selectedMic = audioInputs.find(
        (mic) => mic.deviceId === selectedMicId,
      );
      const savedMic = audioInputs.find((mic) => mic.deviceId === savedMicId);
      const bestMic = getBestAudioInputDevice(audioInputs);

      if (!deviceIdForRecording && (savedMic || bestMic)) {
        const micToUse = savedMic || bestMic;
        deviceIdForRecording = micToUse.deviceId;
        setSelectedMicId(micToUse.deviceId);
        setSavedMicId(micToUse.deviceId);
        setSelectedMicLabel(micToUse.label);
        saveMicPreference(micToUse);
      } else if (selectedMic) {
        saveMicPreference(selectedMic);
      }

      setShowSpeakingPrompt(false);
      if (!navigator.mediaDevices?.getUserMedia) {
        setStatus("Audio recording is not supported in this browser");
        return;
      }

      if (!window.MediaRecorder) {
        setStatus("MediaRecorder is not supported in this browser");
        return;
      }

      stopMicTest();
      stopPreviewStream();
      stopMicLevelMonitor();
      fullRecordingPartsRef.current = [];
      shouldUploadAfterStopRef.current = false;
      pendingUnsafeCloseRef.current = false;
      unsafeCloseChoicePendingRef.current = false;
      resumeRecordingAfterUnsafeCloseCancelRef.current = false;
      setSavedFlash(false);
      setIsPaused(false);
      resetTimer();
      setStatus("Requesting microphone permission...");

      const stream = await navigator.mediaDevices.getUserMedia(
        getAudioConstraints(deviceIdForRecording),
      );

      mediaStreamRef.current = stream;
      startMicLevelMonitor(stream);
      setIsMicPreviewActive(true);

      const mimeType = getBestMimeType();
      const recorder = mimeType
        ? new MediaRecorder(stream, {
            mimeType,
            audioBitsPerSecond: 128000,
          })
        : new MediaRecorder(stream, {
            audioBitsPerSecond: 128000,
          });

      mediaRecorderRef.current = recorder;

      recorder.onstart = () => {
        setIsRecording(true);
        setIsPaused(false);
        setStatus("Recording full session locally...");
        startTimer();
      };

      recorder.onpause = () => {
        setIsPaused(true);
        setStatus("Recording paused. Live mic level is still active.");
        stopTimer();
      };

      recorder.onresume = () => {
        setIsPaused(false);
        setStatus("Recording resumed...");
        startTimer();
      };

      recorder.ondataavailable = (event: BlobEvent) => {
        if (!event.data || event.data.size === 0) return;
        fullRecordingPartsRef.current.push(event.data);
      };

      recorder.onerror = (event) => {
        console.error("Recorder error:", event);
        setStatus("Recording error");
        setIsRecording(false);
        setIsPaused(false);
        stopTimer();
        stopMicLevelMonitor();
        setIsMicPreviewActive(false);
        window.setTimeout(() => startMicPreview(), 250);
      };

      recorder.onstop = async () => {
        stopTimer();
        stopMicLevelMonitor();
        setIsMicPreviewActive(false);

        mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;

        const finalMimeType =
          recorder.mimeType ||
          fullRecordingPartsRef.current[0]?.type ||
          "audio/webm";
        const fullAudioBlob = new Blob(fullRecordingPartsRef.current, {
          type: finalMimeType,
        });

        const shouldDiscardAfterStop = discardRecordingAfterStopRef.current;
        const shouldRestartAfterDiscard =
          restartRecordingAfterDiscardRef.current;

        if (shouldDiscardAfterStop) {
          discardRecordingAfterStopRef.current = false;
          restartRecordingAfterDiscardRef.current = false;
          shouldUploadAfterStopRef.current = false;
          pendingUnsafeCloseRef.current = false;
          fullRecordingPartsRef.current = [];
          setIsRecording(false);
          setIsPaused(false);
          setIsMicPreviewActive(false);
          resetTimer();

          await performClearAllRecordings();

          setStatus(
            shouldRestartAfterDiscard
              ? "Recording discarded. Preparing a fresh recording..."
              : "Recording discarded.",
          );

          if (shouldRestartAfterDiscard) {
            window.setTimeout(() => {
              void startRecording();
            }, 150);
            return;
          }

          window.setTimeout(() => startMicPreview(), 250);
          return;
        }

        if (!fullAudioBlob.size || fullRecordingPartsRef.current.length === 0) {
          shouldUploadAfterStopRef.current = false;
          setStatus("No audio was recorded");
          setIsRecording(false);
          setIsPaused(false);

          if (pendingUnsafeCloseRef.current) {
            pendingUnsafeCloseRef.current = false;
            onRequestClose?.();
            return;
          }

          window.setTimeout(() => startMicPreview(), 250);
          return;
        }

        const shouldUploadAfterStop = shouldUploadAfterStopRef.current;
        shouldUploadAfterStopRef.current = false;

        let savedRecording: SavedRecording | null = null;

        try {
          savedRecording = await addSavedRecordingFromBlob(fullAudioBlob, {
            durationSeconds: recordingSecondsRef.current,
          });
        } catch (error: any) {
          console.error(error);
          setStatus(error?.message || "Failed to save recording locally");
        }

        setIsRecording(false);
        setIsPaused(false);
        fullRecordingPartsRef.current = [];

        if (pendingUnsafeCloseRef.current) {
          pendingUnsafeCloseRef.current = false;
          onRequestClose?.();
          return;
        }

        if (shouldUploadAfterStop && savedRecording) {
          setStatus("Recording saved. Uploading to cloud...");
          await uploadRecordingWithPresignedUrls(savedRecording);
          window.setTimeout(() => startMicPreview(), 250);
          return;
        }

        window.setTimeout(() => startMicPreview(), 250);
      };

      recorder.start();
    } catch (error) {
      shouldUploadAfterStopRef.current = false;
      pendingUnsafeCloseRef.current = false;
      console.error(error);
      setStatus("Microphone permission denied or unavailable");
      setIsRecording(false);
      setIsPaused(false);
      setIsMicPreviewActive(false);
      stopTimer();
      stopMicLevelMonitor();
      window.setTimeout(() => startMicPreview(), 250);
    }
  };

  const handleRecordClick = async () => {
    if (recordDisabled) {
      setStatus(recordDisabledMessage);
      return;
    }

    const planId = getNormalizedTreatmentPlanId();

    if (planId && !/^\d+$/.test(planId)) {
      setStatus("Treatment plan ID must contain only numbers.");
      return;
    }

    if (!planId) {
      await startRecording();
      return;
    }

    if (skipTreatmentPlanCheckOnRecord) {
      await startRecording();
      return;
    }

    try {
      setIsCheckingTreatmentPlanAudio(true);
      setStatus(`Checking Treatment Plan ID ${planId}...`);

      const hasExistingAudio = await checkTreatmentPlanAudioExists(planId);
      onTreatmentPlanCheckPassed?.(planId);

      if (hasExistingAudio) {
        setStatus(`Existing recording found for Treatment Plan ID ${planId}.`);
        openExistingAudioWarning(planId);
        return;
      }

      await startRecording();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        error?.message ||
        "";
      const expectedMissingConsentBundleDetail = `check_treatment_plan_id failed: Consent bundle with treatment id: ${planId} does not exist`;
      const isMissingConsentBundle =
        String(errorMessage).trim() === expectedMissingConsentBundleDetail;

      if (isMissingConsentBundle && onCreateConsentBundleIfMissing) {
        try {
          setIsCreatingConsentBundle(true);
          setStatus(
            `Creating consent bundle for Treatment Plan ID ${planId}...`,
          );

          await onCreateConsentBundleIfMissing(planId);

          setStatus("Consent bundle created. Starting recording...");
          await startRecording();
          return;
        } catch (creationError: any) {
          console.error("Error creating consent bundle:", creationError);
          setStatus(
            creationError?.response?.data?.detail ||
              creationError?.response?.data?.error ||
              creationError?.message ||
              "Failed to create the consent bundle. Please try again.",
          );
          return;
        } finally {
          setIsCreatingConsentBundle(false);
        }
      }

      console.error("Error checking existing audio record:", error);
      setStatus(
        errorMessage ||
          "Failed to check existing audio record. Please try again.",
      );
    } finally {
      setIsCheckingTreatmentPlanAudio(false);
    }
  };

  const handleStopAndTranscribe = () => {
    if (!canUploadAndTranscribe) {
      setStatus(uploadDisabledMessage);
      return;
    }

    askToStartTranscriptionUpload();
  };

  const performPauseRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder?.state === "recording") recorder.pause();
  };

  const performResumeRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder?.state === "paused") recorder.resume();
  };

  const performStopRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder?.state === "recording" || recorder?.state === "paused") {
      setStatus("Stopping and saving full recording...");
      recorder.stop();
    }
  };

  const stopAllSavedPlayers = () => {
    Object.values(audioRefs.current).forEach((audio) => audio?.pause());
    setActivePlaybackId(null);
  };

  const performDeleteRecording = async (id: string) => {
    const audio = audioRefs.current[id];

    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }

    delete audioRefs.current[id];
    await deleteBlobFromDb(id).catch(() => undefined);

    const deletedRecording = recordings.find(
      (recording) => recording.id === id,
    );
    if (deletedRecording?.objectUrl)
      URL.revokeObjectURL(deletedRecording.objectUrl);

    const updatedRecordings = recordings.filter((item) => item.id !== id);
    syncRecordings(updatedRecordings);

    const remainingJobs = getPendingUploadJobs().filter(
      (job) => job.recordingId !== id,
    );
    savePendingUploadJobs(remainingJobs);

    setStatus("Recording deleted");

    setPlayerStates((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });

    if (activePlaybackId === id) setActivePlaybackId(null);
    if (selectedRecordingId === id)
      setSelectedRecordingId(updatedRecordings[0]?.id || "");
    if (updatedRecordings.length === 0) setIsPreviewOpen(false);
  };

  const performClearAllRecordings = async () => {
    stopAllSavedPlayers();
    audioRefs.current = {};

    await Promise.all(
      recordings.map(async (recording) => {
        await deleteBlobFromDb(recording.id).catch(() => undefined);
        if (recording.objectUrl) URL.revokeObjectURL(recording.objectUrl);
      }),
    );

    localStorage.removeItem(META_STORAGE_KEY);
    localStorage.removeItem(PENDING_UPLOADS_KEY);
    setRecordings([]);
    setSelectedRecordingId("");
    setIsPreviewOpen(false);
    setPlayerStates({});
    setStatus("All recordings cleared");
  };

  const handleRetakeRecording = async () => {
    closeConfirmModal();
    closeWorkflowOverlay();
    shouldUploadAfterStopRef.current = false;
    pendingUnsafeCloseRef.current = false;

    const recorder = mediaRecorderRef.current;

    if (recorder?.state === "recording" || recorder?.state === "paused") {
      discardRecordingAfterStopRef.current = true;
      restartRecordingAfterDiscardRef.current = true;
      setStatus("Discarding current recording and starting again...");
      stopTimer();
      resetTimer();

      try {
        recorder.stop();
      } catch (error) {
        console.error("Failed to stop current recording before retake:", error);
        discardRecordingAfterStopRef.current = false;
        restartRecordingAfterDiscardRef.current = false;
        await performClearAllRecordings();
        window.setTimeout(() => {
          void startRecording();
        }, 120);
      }

      return;
    }

    discardRecordingAfterStopRef.current = false;
    restartRecordingAfterDiscardRef.current = false;
    setStatus("Preparing a fresh recording...");
    await performClearAllRecordings();
    window.setTimeout(() => {
      void startRecording();
    }, 120);
  };

  const handleRetakeFromConfirmModal = () => {
    shouldUploadAfterStopRef.current = false;
    void handleRetakeRecording();
  };

  const handleCancelConfirmAction = () => {
    const action = confirmModal?.action;
    closeConfirmModal();

    if (action === "replaceExistingAudio") {
      pendingExistingAudioConfirmRef.current = null;
    }

    if (action === "startTranscribe") {
      shouldUploadAfterStopRef.current = false;
      performResumeRecording();
      setStatus("Recording resumed. Upload cancelled.");
    }

    if (action === "unsafeClose") {
      unsafeCloseChoicePendingRef.current = false;
      const shouldResumeRecording =
        resumeRecordingAfterUnsafeCloseCancelRef.current;
      resumeRecordingAfterUnsafeCloseCancelRef.current = false;

      if (shouldResumeRecording) {
        performResumeRecording();
        setStatus("Recording resumed.");
      }
    }
  };

  const handlePauseFromConfirmModal = () => {
    closeConfirmModal();
    performPauseRecording();
  };

  const handleConfirmAction = () => {
    if (!confirmModal) return;

    const { action, recordingId } = confirmModal;
    closeConfirmModal();

    switch (action) {
      case "pause":
        performPauseRecording();
        break;
      case "resume":
        performResumeRecording();
        break;
      case "stop":
        performStopRecording();
        break;
      case "startTranscribe":
        if (!canUploadAndTranscribe) {
          shouldUploadAfterStopRef.current = false;
          setStatus(uploadDisabledMessage);
          break;
        }

        shouldUploadAfterStopRef.current = true;
        setStatus("Saving recording and starting transcription upload...");
        performStopRecording();
        break;
      case "replaceExistingAudio":
        if (pendingExistingAudioConfirmRef.current) {
          const continuePendingAction = pendingExistingAudioConfirmRef.current;
          pendingExistingAudioConfirmRef.current = null;
          continuePendingAction();
        } else {
          void startRecording();
        }
        break;
      case "delete":
        if (recordingId) void performDeleteRecording(recordingId);
        break;
      case "clearAll":
        void performClearAllRecordings();
        break;
      case "unsafeClose":
        unsafeCloseChoicePendingRef.current = false;
        resumeRecordingAfterUnsafeCloseCancelRef.current = false;
        if (isRecording) {
          pendingUnsafeCloseRef.current = true;
          shouldUploadAfterStopRef.current = false;
          performStopRecording();
        } else {
          onRequestClose?.();
        }
        break;
      default:
        break;
    }
  };

  const updatePlayerState = (id: string, nextState: Partial<PlayerState>) => {
    setPlayerStates((prev) => ({
      ...prev,
      [id]: {
        currentTime: prev[id]?.currentTime || 0,
        duration: prev[id]?.duration || 0,
        ...nextState,
      },
    }));
  };

  const handleLoadedMetadata = (id: string) => {
    const audio = audioRefs.current[id];
    if (!audio) return;

    updatePlayerState(id, {
      currentTime: audio.currentTime || 0,
      duration: Number.isFinite(audio.duration) ? audio.duration : 0,
    });
  };

  const handleTimeUpdate = (id: string) => {
    const audio = audioRefs.current[id];
    if (!audio) return;

    updatePlayerState(id, {
      currentTime: audio.currentTime || 0,
      duration: Number.isFinite(audio.duration) ? audio.duration : 0,
    });
  };

  const handlePlayerEnded = (id: string) => {
    const audio = audioRefs.current[id];
    if (audio) audio.currentTime = 0;
    updatePlayerState(id, { currentTime: 0 });
    if (activePlaybackId === id) setActivePlaybackId(null);
  };

  const handleTogglePlayback = async (id: string) => {
    const selectedAudio = audioRefs.current[id];
    if (!selectedAudio) return;

    if (activePlaybackId === id && !selectedAudio.paused) {
      selectedAudio.pause();
      setActivePlaybackId(null);
      return;
    }

    Object.entries(audioRefs.current).forEach(([recordingId, audio]) => {
      if (!audio || recordingId === id) return;
      audio.pause();
    });

    try {
      await selectedAudio.play();
      setActivePlaybackId(id);
    } catch (error) {
      console.error("Failed to play recording:", error);
      setStatus("Could not play this recording");
    }
  };

  const handleSeekRecording = (id: string, value: string) => {
    const audio = audioRefs.current[id];
    if (!audio) return;

    const nextTime = Number(value);
    audio.currentTime = nextTime;

    updatePlayerState(id, {
      currentTime: nextTime,
      duration: Number.isFinite(audio.duration) ? audio.duration : 0,
    });
  };

  const handleSelectRecording = (id: string) => {
    setSelectedRecordingId(id);
    setIsPreviewOpen(false);
    setStatus("Recording selected. Ready to preview.");
  };

  const handleDroppedFiles = async (
    files: FileList | File[],
    options?: { autoUpload?: boolean; skipExistingAudioCheck?: boolean },
  ) => {
    const file = Array.from(files)[0];
    if (!file) return;

    if (isRecording) {
      setStatus("Stop the current recording before replacing the audio file.");
      return;
    }

    if (!isAudioFile(file)) {
      setStatus("Please upload a valid audio file.");
      return;
    }

    const planId = getNormalizedTreatmentPlanId();

    if (options?.autoUpload && !planId) {
      setStatus(uploadDisabledMessage);
      return;
    }

    if (options?.autoUpload && !/^\d+$/.test(planId)) {
      setStatus("Treatment plan ID must contain only numbers.");
      return;
    }

    if (options?.autoUpload && !options.skipExistingAudioCheck) {
      try {
        setIsCheckingTreatmentPlanAudio(true);
        setStatus(`Checking Treatment Plan ID ${planId} before upload...`);

        const hasExistingAudio = await checkTreatmentPlanAudioExists(planId);

        if (hasExistingAudio) {
          setStatus(
            `Existing recording found for Treatment Plan ID ${planId}.`,
          );
          openExistingAudioWarning(planId, () => {
            void handleDroppedFiles([file], {
              autoUpload: true,
              skipExistingAudioCheck: true,
            });
          });
          return;
        }
      } catch (error: any) {
        console.error("Error checking existing audio before upload:", error);
        setStatus(
          error?.response?.data?.detail ||
            error?.response?.data?.error ||
            error?.message ||
            "Failed to check existing audio record. Please try again.",
        );
        return;
      } finally {
        setIsCheckingTreatmentPlanAudio(false);
      }
    }

    const hadExistingAudio =
      recordings.length > 0 || isUploadInProgress || isWorkflowInProgress;

    cancelCurrentAudioWorkflowForReplacement();
    const workflowToken = beginFreshAudioWorkflow();

    try {
      setStatus(
        options?.autoUpload
          ? hadExistingAudio
            ? "Replacing old audio and starting upload..."
            : "Saving uploaded audio and starting upload..."
          : hadExistingAudio
            ? "Replacing old audio locally..."
            : "Saving uploaded audio locally...",
      );

      stopAllSavedPlayers();

      const savedRecording = await addSavedRecordingFromBlob(file, {
        fileName: file.name,
        durationSeconds: await getAudioDurationSeconds(file),
      });

      setIsPreviewOpen(true);

      if (options?.autoUpload) {
        if (!canUploadAndTranscribe) {
          setStatus(uploadDisabledMessage);
          return;
        }

        await uploadRecordingWithPresignedUrls(savedRecording, {
          workflowToken,
        });
      } else {
        setStatus(
          hadExistingAudio
            ? "Old audio replaced. Ready to upload."
            : "Audio saved locally. Ready to upload.",
        );
      }
    } catch (error: any) {
      if (isAudioReplacedError(error)) return;
      console.error(error);
      setStatus(error?.message || "Failed to add uploaded audio.");
    }
  };

  const handleDebugPreviewFiles = async (files: FileList | File[]) => {
    const file = Array.from(files)[0];
    setIsPreviewDraggingFile(false);

    if (!file || isCheckingTreatmentPlanAudio) return;

    if (isRecording) {
      setStatus("Stop the current recording before replacing the audio file.");
      return;
    }

    if (!isAudioFile(file)) {
      setStatus("Please upload a valid audio file.");
      return;
    }

    await handleDroppedFiles([file], { autoUpload: true });
  };

  const handleDebugS3OnlyUpload = async (
    files: FileList | File[],
    options?: { skipExistingAudioCheck?: boolean },
  ) => {
    const file = Array.from(files)[0];
    setIsDebugS3DraggingFile(false);

    if (!file || isCheckingTreatmentPlanAudio) return;
    if (
      debugS3Upload.status === "requesting" ||
      debugS3Upload.status === "uploading"
    ) {
      return;
    }

    if (!isAudioFile(file)) {
      setDebugS3Upload({
        status: "failed",
        fileName: file.name,
        progress: 0,
        uploadSessionId: "",
        uploadedParts: 0,
        totalParts: 0,
        errorMessage: "Please select a valid audio file.",
      });
      return;
    }

    const planId = getNormalizedTreatmentPlanId();

    if (!planId) {
      setDebugS3Upload({
        status: "failed",
        fileName: file.name,
        progress: 0,
        uploadSessionId: "",
        uploadedParts: 0,
        totalParts: 0,
        errorMessage: uploadDisabledMessage,
      });
      return;
    }

    if (!/^\d+$/.test(planId)) {
      setDebugS3Upload({
        status: "failed",
        fileName: file.name,
        progress: 0,
        uploadSessionId: "",
        uploadedParts: 0,
        totalParts: 0,
        errorMessage: "Treatment plan ID must contain only numbers.",
      });
      return;
    }

    if (!options?.skipExistingAudioCheck) {
      try {
        setIsCheckingTreatmentPlanAudio(true);
        setStatus(
          `Checking Treatment Plan ID ${planId} before debug S3 upload...`,
        );

        const hasExistingAudio = await checkTreatmentPlanAudioExists(planId);

        if (hasExistingAudio) {
          setStatus(
            `Existing recording found for Treatment Plan ID ${planId}.`,
          );
          openExistingAudioWarning(planId, () => {
            void handleDebugS3OnlyUpload([file], {
              skipExistingAudioCheck: true,
            });
          });
          return;
        }
      } catch (error: any) {
        console.error(
          "Error checking existing audio before debug S3 upload:",
          error,
        );
        setDebugS3Upload({
          status: "failed",
          fileName: file.name,
          progress: 0,
          uploadSessionId: "",
          uploadedParts: 0,
          totalParts: 0,
          errorMessage:
            error?.response?.data?.detail ||
            error?.response?.data?.error ||
            error?.message ||
            "Failed to check existing audio record. Please try again.",
        });
        return;
      } finally {
        setIsCheckingTreatmentPlanAudio(false);
      }
    }

    try {
      setDebugS3Upload({
        status: "requesting",
        fileName: file.name,
        progress: 0,
        uploadSessionId: "",
        uploadedParts: 0,
        totalParts: 0,
        errorMessage: "",
      });

      const durationSeconds = await getAudioDurationSeconds(file);
      const createdAt = new Date().toISOString();
      const debugRecording: SavedRecording = {
        id: createId(),
        name: file.name,
        mimeType: file.type || "application/octet-stream",
        size: file.size,
        durationSeconds,
        durationText: formatTime(durationSeconds),
        createdAt,
        objectUrl: "",
        uploadStatus: "idle",
        uploadProgress: 0,
      };

      // Debug-only path after the treatment-plan pre-check.
      const generateResponse = await generateUploadUrls(debugRecording, file);
      const totalParts = generateResponse.urls.length;

      if (totalParts === 0) {
        throw new Error("The backend did not return any presigned URLs.");
      }

      setDebugS3Upload((current) => ({
        ...current,
        status: "uploading",
        uploadSessionId: generateResponse.upload_session_id,
        totalParts,
      }));

      const uploadedPartResults: UploadPartResult[] = [];

      for (let index = 0; index < generateResponse.urls.length; index += 1) {
        const urlItem = generateResponse.urls[index];
        const uploadUrl = getUploadUrl(urlItem);
        const partNumber = getPartNumber(urlItem, index);

        if (!uploadUrl) {
          throw new Error(`Missing presigned URL for part ${partNumber}.`);
        }

        const start = (partNumber - 1) * generateResponse.part_size;
        const end = Math.min(start + generateResponse.part_size, file.size);
        const partBlob = file.slice(
          start,
          end,
          file.type || "application/octet-stream",
        );

        console.log("[DEV DEBUG][S3] PUT START", {
          PartNumber: partNumber,
          Size: partBlob.size,
        });

        const uploadedPart = await uploadOnePart(
          uploadUrl,
          partBlob,
          partNumber,
          file.type || "application/octet-stream",
        );

        console.log("[DEV DEBUG][S3] PUT SUCCESS", {
          PartNumber: uploadedPart.part_number,
          ETag: uploadedPart.etag,
        });

        if (!uploadedPart.etag) {
          console.warn(
            `[DEV DEBUG][S3] Part ${uploadedPart.part_number} uploaded, but the browser could not read its ETag. ` +
              'Expose the ETag header in the S3 bucket CORS configuration using "ExposeHeaders": ["ETag"].',
          );
        }
        uploadedPartResults.push(uploadedPart);

        const uploadedParts = index + 1;
        setDebugS3Upload((current) => ({
          ...current,
          status: "uploading",
          progress: Math.round((uploadedParts / totalParts) * 100),
          uploadedParts,
        }));
      }

      setDebugS3Upload((current) => ({
        ...current,
        status: "partsUploaded",
        progress: 100,
        uploadedParts: totalParts,
        errorMessage: "",
      }));

      const uploadedEtags = uploadedPartResults.map(
        ({ part_number, etag }) => ({
          PartNumber: part_number,
          ETag: etag,
        }),
      );

      console.log("[DEV DEBUG][S3] ALL PARTS UPLOADED", uploadedEtags);
      console.table(uploadedEtags);
    } catch (error: any) {
      console.error("Debug S3-only upload failed:", error);
      setDebugS3Upload((current) => ({
        ...current,
        status: "failed",
        errorMessage: error?.message || "Debug S3-only upload failed.",
      }));
    }
  };

  useEffect(() => {
    if (isRecording || !isMicPreviewActive) {
      if (speakingPromptTimerRef.current) {
        window.clearTimeout(speakingPromptTimerRef.current);
        speakingPromptTimerRef.current = null;
      }
      setShowSpeakingPrompt(false);
      return;
    }

    if (micLevel >= 14) {
      if (!speakingPromptTimerRef.current) {
        speakingPromptTimerRef.current = window.setTimeout(() => {
          setShowSpeakingPrompt(true);
          speakingPromptTimerRef.current = null;
        }, 900);
      }
      return;
    }

    if (speakingPromptTimerRef.current) {
      window.clearTimeout(speakingPromptTimerRef.current);
      speakingPromptTimerRef.current = null;
    }
    setShowSpeakingPrompt(false);
  }, [isRecording, isMicPreviewActive, micLevel]);

  useEffect(() => {
    const nextUnsafeState: AudioUnsafeCloseState = {
      isUnsafe: shouldConfirmUnsafeExit,
      reason: unsafeExitReason,
      title: unsafeExitTitle,
      message: unsafeExitMessage,
      isRecording,
      isUploadInProgress,
      isWorkflowInProgress,
    };

    const previousUnsafeState = lastUnsafeStateRef.current;
    const didUnsafeStateChange =
      !previousUnsafeState ||
      previousUnsafeState.isUnsafe !== nextUnsafeState.isUnsafe ||
      previousUnsafeState.reason !== nextUnsafeState.reason ||
      previousUnsafeState.title !== nextUnsafeState.title ||
      previousUnsafeState.message !== nextUnsafeState.message ||
      previousUnsafeState.isRecording !== nextUnsafeState.isRecording ||
      previousUnsafeState.isUploadInProgress !==
        nextUnsafeState.isUploadInProgress ||
      previousUnsafeState.isWorkflowInProgress !==
        nextUnsafeState.isWorkflowInProgress;

    if (!didUnsafeStateChange) return;

    lastUnsafeStateRef.current = nextUnsafeState;
    onUnsafeStateChangeRef.current?.(nextUnsafeState);
  }, [
    shouldConfirmUnsafeExit,
    unsafeExitReason,
    unsafeExitTitle,
    unsafeExitMessage,
    isRecording,
    isUploadInProgress,
    isWorkflowInProgress,
  ]);

  useEffect(() => {
    if (!shouldConfirmUnsafeExit) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = unsafeExitMessage;
      return unsafeExitMessage;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [shouldConfirmUnsafeExit, unsafeExitMessage]);

  useEffect(() => {
    if (!shouldConfirmUnsafeExit) return;

    const stopCloseEvent = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === "function") {
        event.stopImmediatePropagation();
      }
    };

    const shouldHandleCloseClick = (target: HTMLElement | null) => {
      if (!target) return false;

      const recorderRoot = document.querySelector(".audio-recorder");
      const modalRoot =
        recorderRoot?.closest(".MuiModal-root, .MuiDialog-root") ||
        recorderRoot?.closest("[role='dialog']");

      if (!modalRoot) return false;

      const targetIsInsideModal = modalRoot.contains(target);
      const targetIsBackdrop = Boolean(target.closest(".MuiBackdrop-root"));

      if (!targetIsInsideModal && !targetIsBackdrop) return false;

      const clickedInsideRecorder = Boolean(target.closest(".audio-recorder"));
      const clickedBackdrop = Boolean(
        target.closest(".MuiBackdrop-root") ||
          (target.classList?.contains("MuiDialog-container") &&
            !clickedInsideRecorder),
      );
      const clickedCloseControl = Boolean(
        target.closest(
          "button[aria-label*='close' i], [aria-label*='close' i], button[title*='close' i], [title*='close' i], [data-testid='CloseIcon'], .MuiSvgIcon-root[data-testid='CloseIcon']",
        ),
      );

      return clickedBackdrop || (clickedCloseControl && !clickedInsideRecorder);
    };

    const handleDocumentClick = (event: MouseEvent) => {
      if (confirmModal?.action === "unsafeClose") return;
      const target = event.target as HTMLElement | null;

      if (!shouldHandleCloseClick(target)) return;

      stopCloseEvent(event);
      askToCloseAudioModal();
    };

    const handleDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || confirmModal?.action === "unsafeClose")
        return;

      stopCloseEvent(event);
      askToCloseAudioModal();
    };

    document.addEventListener("click", handleDocumentClick, true);
    document.addEventListener("mousedown", handleDocumentClick, true);
    document.addEventListener("keydown", handleDocumentKeyDown, true);

    return () => {
      document.removeEventListener("click", handleDocumentClick, true);
      document.removeEventListener("mousedown", handleDocumentClick, true);
      document.removeEventListener("keydown", handleDocumentKeyDown, true);
    };
  }, [
    shouldConfirmUnsafeExit,
    confirmModal,
    unsafeExitMessage,
    unsafeExitTitle,
    isRecording,
    onRequestClose,
  ]);

  useEffect(() => {
    if (!shouldRestorePersistedWorkflowRef.current) return;

    const syncRestoredWorkflow = () => {
      const persistedWorkflow = getActiveAudioWorkflowForTreatmentPlan(
        treatmentPlanId,
        consentBundleId,
      );
      if (!persistedWorkflow) return;

      activePersistedWorkflowRef.current = persistedWorkflow;
      setWorkflowOverlay({
        isOpen: true,
        stage: persistedWorkflow.stage,
        progress: persistedWorkflow.progress,
        currentStatus:
          persistedWorkflow.currentStatus || "Audio processing in progress",
        retryAction: persistedWorkflow.retryAction,
        failedUploadSessionId:
          persistedWorkflow.retryAction === "transcription"
            ? persistedWorkflow.uploadSessionId
            : undefined,
      });

      if (
        [
          "uploadComplete",
          "transcribing",
          "transcriptionComplete",
          "generatingPatientNote",
        ].includes(persistedWorkflow.stage) &&
        !persistedProcessingResumeStartedRef.current
      ) {
        void resumePersistedAudioWorkflow();
      }
    };

    window.addEventListener(ACTIVE_AUDIO_WORKFLOW_EVENT, syncRestoredWorkflow);
    window.addEventListener("storage", syncRestoredWorkflow);

    return () => {
      window.removeEventListener(
        ACTIVE_AUDIO_WORKFLOW_EVENT,
        syncRestoredWorkflow,
      );
      window.removeEventListener("storage", syncRestoredWorkflow);
    };
    // The persisted workflow is intentionally restored only for the treatment
    // plan that was active when this recorder instance mounted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let isMounted = true;

    const hydrateRecordings = async () => {
      const storedMeta = getStoredRecordingMeta().slice(0, localRecordingLimit);
      const hydrated: SavedRecording[] = [];

      for (const meta of storedMeta) {
        const blob = await getBlobFromDb(meta.id).catch(() => null);
        if (!blob) continue;

        const objectUrl = URL.createObjectURL(blob);
        objectUrlsRef.current.push(objectUrl);
        hydrated.push({ ...meta, objectUrl });
      }

      if (!isMounted) return;
      setRecordings(hydrated);
      setSelectedRecordingId(hydrated[0]?.id || "");
      setIsPreviewOpen(false);
    };

    void hydrateRecordings();
    void loadAudioInputDevices();
    void resumePersistedAudioWorkflow();

    retryTimerRef.current = window.setInterval(() => {
      void retryPendingUploads();
    }, retryPollMs);

    const handleDeviceChange = () => void loadAudioInputDevices();
    navigator.mediaDevices?.addEventListener?.(
      "devicechange",
      handleDeviceChange,
    );

    return () => {
      isMounted = false;
      stopTimer();
      stopMicLevelMonitor();
      stopPreviewStream();
      stopMicTest();

      if (retryTimerRef.current) window.clearInterval(retryTimerRef.current);
      if (speakingPromptTimerRef.current)
        window.clearTimeout(speakingPromptTimerRef.current);
      if (micCoachTimerRef.current)
        window.clearTimeout(micCoachTimerRef.current);
      if (patientNoteCopyTimerRef.current)
        window.clearTimeout(patientNoteCopyTimerRef.current);

      navigator.mediaDevices?.removeEventListener?.(
        "devicechange",
        handleDeviceChange,
      );

      mediaStreamRef.current?.getTracks().forEach((track) => track.stop());

      Object.values(audioRefs.current).forEach((audio) => audio?.pause());
      audioRefs.current = {};
      objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      objectUrlsRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!patientNoteEditor.isOpen) return;

    let cancelled = false;
    const textarea = patientNoteTextareaRef.current;
    const textareaId = `audio-recorder-patient-note-ckeditor-${
      patientNoteEditor.uploadSessionId || "new"
    }`;

    const cleanupScrollListener = () => {
      if (patientNoteCkScrollCleanupRef.current) {
        patientNoteCkScrollCleanupRef.current();
        patientNoteCkScrollCleanupRef.current = null;
      }
    };

    const attachEditorScrollListener = (instance: any) => {
      cleanupScrollListener();

      const iframeDocument = instance?.document?.$ as Document | undefined;
      const iframeWindow = iframeDocument?.defaultView;
      const iframeBody = iframeDocument?.body;
      const onScroll = () => handlePatientNoteEditorScroll();
      const onUserInteraction = () => markPatientNoteReviewInteraction();
      const onKeyDown = (event: KeyboardEvent) => {
        if (
          [
            "ArrowDown",
            "ArrowUp",
            "PageDown",
            "PageUp",
            "End",
            "Home",
            " ",
            "Spacebar",
          ].includes(event.key)
        ) {
          markPatientNoteReviewInteraction();
        }
      };

      iframeWindow?.addEventListener("scroll", onScroll, { passive: true });
      iframeDocument?.addEventListener("scroll", onScroll, { passive: true });
      iframeBody?.addEventListener("scroll", onScroll, { passive: true });

      iframeWindow?.addEventListener("wheel", onUserInteraction, {
        passive: true,
      });
      iframeDocument?.addEventListener("wheel", onUserInteraction, {
        passive: true,
      });
      iframeBody?.addEventListener("wheel", onUserInteraction, {
        passive: true,
      });

      iframeWindow?.addEventListener("touchmove", onUserInteraction, {
        passive: true,
      });
      iframeDocument?.addEventListener("touchmove", onUserInteraction, {
        passive: true,
      });
      iframeBody?.addEventListener("touchmove", onUserInteraction, {
        passive: true,
      });

      iframeWindow?.addEventListener("mousedown", onUserInteraction);
      iframeDocument?.addEventListener("mousedown", onUserInteraction);
      iframeBody?.addEventListener("mousedown", onUserInteraction);

      iframeWindow?.addEventListener("keydown", onKeyDown);
      iframeDocument?.addEventListener("keydown", onKeyDown);
      iframeBody?.addEventListener("keydown", onKeyDown);

      patientNoteCkScrollCleanupRef.current = () => {
        iframeWindow?.removeEventListener("scroll", onScroll);
        iframeDocument?.removeEventListener("scroll", onScroll);
        iframeBody?.removeEventListener("scroll", onScroll);

        iframeWindow?.removeEventListener("wheel", onUserInteraction);
        iframeDocument?.removeEventListener("wheel", onUserInteraction);
        iframeBody?.removeEventListener("wheel", onUserInteraction);

        iframeWindow?.removeEventListener("touchmove", onUserInteraction);
        iframeDocument?.removeEventListener("touchmove", onUserInteraction);
        iframeBody?.removeEventListener("touchmove", onUserInteraction);

        iframeWindow?.removeEventListener("mousedown", onUserInteraction);
        iframeDocument?.removeEventListener("mousedown", onUserInteraction);
        iframeBody?.removeEventListener("mousedown", onUserInteraction);

        iframeWindow?.removeEventListener("keydown", onKeyDown);
        iframeDocument?.removeEventListener("keydown", onKeyDown);
        iframeBody?.removeEventListener("keydown", onKeyDown);
      };
    };

    const initEditor = async () => {
      if (!textarea) return;

      try {
        await loadCKEditorScript();
        if (cancelled) return;

        const CKEDITOR = (window as any).CKEDITOR;
        if (!CKEDITOR) return;

        textarea.id = textareaId;

        if (CKEDITOR.instances?.[textareaId]) {
          CKEDITOR.instances[textareaId].destroy(true);
        }

        const instance = CKEDITOR.replace(
          textareaId,
          getPatientNoteCKEditorConfig(360),
        );

        patientNoteCkEditorRef.current = instance;

        instance.on("instanceReady", () => {
          if (cancelled) return;

          instance.setData(
            patientNoteEditor.patientNoteHtml ||
              formatPatientNoteHtmlForEditor(patientNoteEditor.patientNoteText),
            {
              callback: () => {
                instance.setReadOnly(Boolean(patientNoteEditor.isSaving));
                attachEditorScrollListener(instance);

                patientNoteReviewUserInteractedRef.current = false;
                setIsPatientNoteReviewedToBottom(false);
              },
            },
          );
        });

        instance.on("change", syncPatientNoteFromCKEditor);
      } catch (error) {
        console.error("Failed to initialise patient note CKEditor:", error);
      }
    };

    initEditor();

    return () => {
      cancelled = true;
      cleanupScrollListener();

      const CKEDITOR = (window as any).CKEDITOR;

      if (CKEDITOR?.instances?.[textareaId]) {
        try {
          CKEDITOR.instances[textareaId].destroy(true);
        } catch (_error) {}
      }

      if (patientNoteCkEditorRef.current?.name === textareaId) {
        patientNoteCkEditorRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientNoteEditor.isOpen, patientNoteEditor.uploadSessionId]);

  useEffect(() => {
    const instance = patientNoteCkEditorRef.current;

    if (instance?.status === "ready") {
      instance.setReadOnly(Boolean(patientNoteEditor.isSaving));
    }
  }, [patientNoteEditor.isSaving]);

  useEffect(() => {
    patientNoteReviewUserInteractedRef.current = false;
    setIsPatientNoteReviewedToBottom(false);

    if (patientNoteEditor.isOpen) {
      setIsPatientNoteCopied(false);
    }
  }, [patientNoteEditor.isOpen, patientNoteEditor.uploadSessionId]);

  const patientNoteReviewMessage = isPatientNoteReviewedToBottom
    ? "Patient note reviewed to the bottom. You can now copy or save."
    : "Please scroll to the bottom of the patient note before copying or saving.";

  const isPatientNoteActionDisabled =
    patientNoteEditor.isSaving || !isPatientNoteReviewedToBottom;

  return (
    <div
      className="audio-recorder"
      data-unsafe-exit-active={shouldConfirmUnsafeExit ? "true" : "false"}
    >
      <div
        className={`audio-recorder__card ${
          isRecording ? "audio-recorder__card--recording" : ""
        } ${isPaused ? "audio-recorder__card--paused" : ""} ${
          savedFlash ? "audio-recorder__card--saved" : ""
        } ${
          patientNoteEditor.isOpen
            ? "audio-recorder__card--hiddenForPatientNote"
            : ""
        }`}
      >
        {workflowOverlay.isOpen && (
          <div
            className={`audio-recorder__workflowOverlay ${
              workflowStageMeta.isError
                ? "audio-recorder__workflowOverlay--error"
                : workflowStageMeta.isComplete
                  ? "audio-recorder__workflowOverlay--complete"
                  : ""
            }`}
            role="dialog"
            aria-modal="true"
            aria-live="polite"
          >
            <div className="audio-recorder__workflowPanel audio-recorder__workflowPanel--simple">
              <div className="audio-recorder__workflowGlow" />

              <div className="audio-recorder__workflowLottie audio-recorder__workflowLottie--simple">
                <Lottie
                  animationData={workflowStageMeta.animationData}
                  autoplay
                  loop={
                    !workflowStageMeta.isComplete && !workflowStageMeta.isError
                  }
                />
              </div>

              <h3 className="audio-recorder__workflowSimpleTitle">
                {workflowStageMeta.title}
              </h3>

              <div
                className={`audio-recorder__simpleProgress ${
                  workflowProgressIsIndeterminate
                    ? "audio-recorder__simpleProgress--indeterminate"
                    : ""
                } ${
                  workflowStageMeta.isError
                    ? "audio-recorder__simpleProgress--error"
                    : ""
                }`}
              >
                <div className="audio-recorder__simpleProgressTrack">
                  <div
                    className="audio-recorder__simpleProgressFill"
                    style={{ width: `${workflowProgressWidth}%` }}
                  />
                  {workflowProgressIsIndeterminate && (
                    <div className="audio-recorder__simpleProgressPulse" />
                  )}
                  <span className="audio-recorder__simpleProgressText">
                    {workflowProgressText}
                  </span>
                </div>
              </div>

              {workflowOverlay.stage === "failed" &&
                workflowOverlay.errorMessage && (
                  <p className="audio-recorder__workflowErrorMessage">
                    {workflowOverlay.errorMessage}
                  </p>
                )}

              {workflowOverlay.stage === "failed" && (
                <div className="audio-recorder__workflowFailedActions">
                  {workflowOverlay.retryAction === "transcription" ? (
                    <button
                      type="button"
                      className="audio-recorder__workflowReupload"
                      onClick={handleRetryTranscription}
                      disabled={isRecording}
                    >
                      Retry transcription
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="audio-recorder__workflowReupload"
                      onClick={handleReuploadFromFailure}
                      disabled={
                        isRecording ||
                        !workflowFailedRecording ||
                        workflowFailedRecording.uploadStatus === "uploading"
                      }
                    >
                      Re-upload
                    </button>
                  )}

                  {/* <button
                    type="button"
                    className="audio-recorder__workflowRetake audio-recorder__retakeIconButton"
                    onClick={handleRetakeRecording}
                    title="Retake"
                    aria-label="Retake"
                  >
                    <RetakeIcon />
                  </button> */}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="audio-recorder__dropAccordion">
          {isDropBoxOpen && (
            <div
              className={`audio-recorder__dropBox ${
                isDraggingFile ? "audio-recorder__dropBox--dragging" : ""
              }`}
              onDragOver={(event) => {
                event.preventDefault();
                setIsDraggingFile(true);
              }}
              onDragLeave={() => setIsDraggingFile(false)}
              onDrop={(event) => {
                event.preventDefault();
                setIsDraggingFile(false);
                void handleDroppedFiles(event.dataTransfer.files, {
                  autoUpload: true,
                });
              }}
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  fileInputRef.current?.click();
                }
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*"
                hidden
                onChange={(event) => {
                  if (event.target.files)
                    void handleDroppedFiles(event.target.files, {
                      autoUpload: true,
                    });
                  event.currentTarget.value = "";
                }}
              />
              <strong>Drop audio file here</strong>
              <span>or click to replace the one saved local recording</span>
            </div>
          )}
        </div>

        <div className="audio-recorder__scrollArea">
          <div className="audio-recorder__stickyControls">
            <div className="audio-recorder__timerWrap">
              <div className="audio-recorder__timer">
                <div
                  className={`audio-recorder__recordIcon ${
                    isRecording && !isPaused
                      ? "audio-recorder__recordIcon--active"
                      : ""
                  } ${isPaused ? "audio-recorder__recordIcon--paused" : ""} ${
                    savedFlash ? "audio-recorder__recordIcon--saved" : ""
                  }`}
                >
                  {savedFlash ? "OK" : isPaused ? "II" : ""}
                </div>

                <span>{formatTime(recordingSeconds)}</span>
                <small>{status}</small>
              </div>

              <div
                className={`audio-recorder__minimalLevel audio-recorder__minimalLevel--live ${
                  isMicPreviewActive
                    ? "audio-recorder__minimalLevel--active"
                    : ""
                } ${hasNoMicInput ? "audio-recorder__minimalLevel--empty" : ""}`}
              >
                <div className="audio-recorder__minimalLevelTop">
                  <strong className="audio-recorder__minimalLevelMicName">
                    {selectedMicLabel}
                  </strong>

                  <button
                    type="button"
                    className={`audio-recorder__settingsButton audio-recorder__settingsButton--inline ${
                      showMicSettings
                        ? "audio-recorder__settingsButton--open"
                        : ""
                    }`}
                    onClick={() => setShowMicSettings((prev) => !prev)}
                    disabled={isRecording}
                    aria-label="Open microphone settings"
                    title="Microphone settings"
                    aria-expanded={showMicSettings}
                  >
                    <span
                      className="audio-recorder__settingsMicIcon"
                      aria-hidden="true"
                    >
                      <svg viewBox="0 0 24 24" focusable="false">
                        <path d="M12 14.75c1.66 0 3-1.34 3-3V6.5c0-1.66-1.34-3-3-3s-3 1.34-3 3v5.25c0 1.66 1.34 3 3 3Z" />
                        <path d="M6.75 11.25a.75.75 0 0 1 1.5 0A3.75 3.75 0 0 0 12 15a3.75 3.75 0 0 0 3.75-3.75.75.75 0 0 1 1.5 0 5.25 5.25 0 0 1-4.5 5.2v2.05h2.5a.75.75 0 0 1 0 1.5h-6.5a.75.75 0 0 1 0-1.5h2.5v-2.05a5.25 5.25 0 0 1-4.5-5.2Z" />
                      </svg>
                    </span>

                    <span
                      className="audio-recorder__settingsDropdownIcon"
                      aria-hidden="true"
                    >
                      <svg viewBox="0 0 24 24" focusable="false">
                        <path d="M7.41 8.59 12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41Z" />
                      </svg>
                    </span>
                  </button>
                </div>

                <div
                  className={`audio-recorder__liveBars ${
                    isRecording
                      ? hasLiveInput
                        ? "audio-recorder__liveBars--hasInput"
                        : "audio-recorder__liveBars--idle"
                      : "audio-recorder__liveBars--flat"
                  }`}
                  aria-hidden="true"
                >
                  {!isRecording ? (
                    <span className="audio-recorder__flatVisualizerLine" />
                  ) : (
                    Array.from({ length: 33 }).map((_, index) => {
                      const centerIndex = 16;
                      const distanceFromCenter = Math.abs(index - centerIndex);
                      const maxDistance = centerIndex;
                      const centerStrength =
                        1 - distanceFromCenter / maxDistance;
                      const idlePulse =
                        10 +
                        Math.sin(Date.now() / 260 + index * 0.45) * 4 +
                        centerStrength * 10;
                      const activeBoost = hasLiveInput
                        ? micLevel * centerStrength * 0.55
                        : 0;
                      const dynamicHeight = Math.max(
                        8,
                        Math.min(58, idlePulse + activeBoost),
                      );

                      return (
                        <i
                          key={index}
                          className={
                            hasLiveInput && dynamicHeight > 18
                              ? "audio-recorder__liveBar--lit"
                              : ""
                          }
                          style={{ height: `${dynamicHeight}px` }}
                        />
                      );
                    })
                  )}
                </div>

                {micCoachNotification && (
                  <div className="audio-recorder__micCoachToast">
                    {micCoachNotification}
                  </div>
                )}
              </div>
            </div>

            <div className="audio-recorder__actions audio-recorder__actions--player audio-recorder__actions--minimal">
              <div className="audio-recorder__transportSpacer" />

              <div className="audio-recorder__transportControls audio-recorder__transportControls--single audio-recorder__transportControls--withRetake">
                {!isRecording ? (
                  <>
                    <button
                      type="button"
                      className={`audio-recorder__transportButton audio-recorder__transportButton--record audio-recorder__transportButton--primaryRecord audio-recorder__transportButton--recordReady ${
                        showSpeakingPrompt
                          ? "audio-recorder__transportButton--attention"
                          : ""
                      }`}
                      onClick={handleRecordClick}
                      disabled={
                        recordDisabled ||
                        isCheckingTreatmentPlanAudio ||
                        isCreatingConsentBundle
                      }
                      title={
                        recordDisabled
                          ? recordDisabledMessage
                          : isCreatingConsentBundle
                            ? "Creating consent bundle"
                            : isCheckingTreatmentPlanAudio
                              ? "Checking treatment plan ID"
                              : "Record"
                      }
                    >
                      <i className="audio-recorder__transportIcon audio-recorder__transportIcon--record" />
                      <span>
                        {recordDisabled
                          ? recordDisabledLabel
                          : isCreatingConsentBundle
                            ? "Creating..."
                            : isCheckingTreatmentPlanAudio
                              ? "Checking..."
                              : "Record"}
                      </span>
                    </button>

                    {/* <button
                      type="button"
                      className="audio-recorder__transportButton audio-recorder__transportButton--retake audio-recorder__retakeIconButton"
                      onClick={handleRetakeRecording}
                      disabled={
                        recordings.length === 0 &&
                        workflowOverlay.stage !== "failed"
                      }
                      title="Retake"
                      aria-label="Retake"
                    >
                      <RetakeIcon />
                    </button> */}
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="audio-recorder__transportButton audio-recorder__transportButton--stop audio-recorder__transportButton--transcribe audio-recorder__transportButton--activeRecording"
                      onClick={handleStopAndTranscribe}
                      disabled={!canUploadAndTranscribe}
                      title={
                        !canUploadAndTranscribe
                          ? uploadDisabledMessage
                          : "Stop & transcribe"
                      }
                    >
                      <i className="audio-recorder__transportIcon audio-recorder__transportIcon--stop" />
                      <span>Stop & transcribe</span>
                    </button>

                    {/* <button
                      type="button"
                      className="audio-recorder__transportButton audio-recorder__transportButton--retake audio-recorder__retakeIconButton"
                      onClick={handleRetakeRecording}
                      title="Retake"
                      aria-label="Retake"
                    >
                      <RetakeIcon />
                    </button> */}
                  </>
                )}
              </div>

              <button
                type="button"
                className="audio-recorder__clearSavedButton audio-recorder__clearSavedButton--hidden"
                onClick={askToClearAllRecordings}
                disabled={recordings.length === 0 || isRecording}
                aria-hidden="true"
                tabIndex={-1}
              >
                Clear saved
              </button>
            </div>
          </div>

          <div
            className={`audio-recorder__micDrawer ${
              showMicSettings ? "audio-recorder__micDrawer--open" : ""
            }`}
          >
            <div className="audio-recorder__micHeader">
              <div>
                <strong>Microphone</strong>
                <span>
                  Select the input device before starting a recording.
                </span>
              </div>

              <button
                type="button"
                className="audio-recorder__refreshMic"
                onClick={loadAudioInputDevices}
                disabled={isRecording}
              >
                Refresh
              </button>
            </div>

            <div className="audio-recorder__micGrid">
              {audioInputs.length === 0 && (
                <div className="audio-recorder__micEmpty">
                  No microphones found.
                </div>
              )}

              {audioInputs.map((mic) => {
                const isSelectedMic = selectedMicId === mic.deviceId;
                const isTestingMic = testingMicId === mic.deviceId;
                const visibleMicLevel = isTestingMic
                  ? micTestLevel
                  : isSelectedMic
                    ? micLevel
                    : 0;

                return (
                  <div
                    key={mic.deviceId}
                    role="button"
                    tabIndex={isRecording ? -1 : 0}
                    aria-disabled={isRecording}
                    className={`audio-recorder__micOption ${
                      isSelectedMic ? "audio-recorder__micOption--active" : ""
                    } ${isTestingMic ? "audio-recorder__micOption--testing" : ""} ${
                      isRecording ? "audio-recorder__micOption--disabled" : ""
                    }`}
                    onClick={() => handleMicChange(mic.deviceId)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        handleMicChange(mic.deviceId);
                      }
                    }}
                  >
                    <span
                      className={`audio-recorder__micSignalBars ${
                        isSelectedMic || isTestingMic
                          ? "audio-recorder__micSignalBars--active"
                          : ""
                      }`}
                      aria-hidden="true"
                    >
                      <i />
                      <i />
                      <i />
                    </span>

                    <span className="audio-recorder__micText">
                      <span className="audio-recorder__micName">
                        {mic.label}
                      </span>
                      <span className="audio-recorder__micHint">
                        {isTestingMic
                          ? "Testing"
                          : getMicInputHint(mic, selectedMicId)}
                      </span>
                      <span
                        className="audio-recorder__micTestMeter"
                        aria-hidden="true"
                      >
                        <span
                          className="audio-recorder__micTestMeterFill"
                          style={{ width: `${visibleMicLevel}%` }}
                        />
                      </span>
                      <span className="audio-recorder__micTestLabel">
                        {isTestingMic
                          ? "Testing..."
                          : isSelectedMic
                            ? "Saved default"
                            : "Test before use"}
                      </span>
                    </span>

                    <span className="audio-recorder__micActions">
                      <button
                        type="button"
                        className={`audio-recorder__micTestButton ${
                          isTestingMic
                            ? "audio-recorder__micTestButton--active"
                            : ""
                        }`}
                        onClick={(event) => {
                          event.stopPropagation();
                          void handleMicTest(mic);
                        }}
                        disabled={isRecording}
                      >
                        {isTestingMic ? "Stop test" : "Test"}
                      </button>

                      <button
                        type="button"
                        className="audio-recorder__micCheck"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleMicChange(mic.deviceId);
                        }}
                        disabled={isRecording || isSelectedMic}
                      >
                        {isSelectedMic ? "Selected" : "Use input"}
                      </button>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="audio-recorder__savedRegion audio-recorder__savedRegion--hidden">
            <div className="audio-recorder__listHeader audio-recorder__listHeader--saved">
              <div>
                <h3>Saved recordings</h3>
                <span>
                  {recordings.length}/{localRecordingLimit} saved locally
                </span>
              </div>

              <button
                type="button"
                className="audio-recorder__primaryUseButton"
                onClick={handleUploadSelected}
                disabled={
                  !selectedRecording ||
                  isRecording ||
                  selectedRecording.uploadStatus === "uploading"
                }
              >
                {selectedRecording?.uploadStatus === "uploading"
                  ? `Uploading ${selectedRecording.uploadProgress || 0}%`
                  : "Upload audio"}
              </button>
            </div>

            {recordings.length === 0 ? (
              <div className="audio-recorder__empty">
                <strong>No saved recordings yet</strong>
                <p>
                  Press Record or drop an audio file. Then select the saved
                  audio and upload it using presigned URLs.
                </p>
              </div>
            ) : (
              <div className="audio-recorder__list">
                {recordings.map((recording) => {
                  const isSelected = selectedRecordingId === recording.id;
                  const isPlaying = activePlaybackId === recording.id;
                  const playerState = playerStates[recording.id] || {
                    currentTime: 0,
                    duration: recording.durationSeconds || 0,
                  };
                  const duration =
                    playerState.duration || recording.durationSeconds || 0;
                  const progress = duration
                    ? Math.min(100, (playerState.currentTime / duration) * 100)
                    : 0;

                  return (
                    <div
                      key={recording.id}
                      className={`audio-recorder__item audio-recorder__item--modern ${
                        isPlaying ? "audio-recorder__item--playing" : ""
                      } ${isSelected ? "audio-recorder__item--selected" : ""}`}
                    >
                      <div className="audio-recorder__modernHeader">
                        <label className="audio-recorder__selectRow">
                          <input
                            type="radio"
                            name="selected-recording"
                            checked={isSelected}
                            onChange={() => handleSelectRecording(recording.id)}
                          />
                          <span>Select this recording</span>
                        </label>

                        <span className="audio-recorder__playingBadge">
                          {recording.uploadStatus === "uploaded"
                            ? "Uploaded"
                            : recording.uploadStatus === "uploading"
                              ? `Uploading ${recording.uploadProgress || 0}%`
                              : recording.uploadStatus === "queued"
                                ? "Retry queued"
                                : recording.uploadStatus === "failed"
                                  ? "Failed"
                                  : recording.uploadStatus === "expired"
                                    ? "Expired"
                                    : isSelected
                                      ? "Selected"
                                      : isPlaying
                                        ? "Playing"
                                        : "Saved"}
                        </span>
                      </div>

                      <div className="audio-recorder__modernTitleBlock">
                        <strong>{recording.name}</strong>
                        <div className="audio-recorder__meta audio-recorder__meta--modern">
                          <span>{recording.durationText}</span>
                          <span>{getReadableFileSize(recording.size)}</span>
                          <span>{recording.mimeType || "audio/webm"}</span>
                        </div>
                      </div>

                      {(recording.uploadStatus === "uploading" ||
                        recording.uploadStatus === "queued" ||
                        recording.uploadStatus === "uploaded") && (
                        <div className="audio-recorder__uploadProgress">
                          <div
                            style={{
                              width: `${recording.uploadProgress || 0}%`,
                            }}
                          />
                        </div>
                      )}

                      {recording.errorMessage && (
                        <div className="audio-recorder__uploadError">
                          {recording.errorMessage}
                        </div>
                      )}

                      <div className="audio-recorder__modernPlayer">
                        <button
                          type="button"
                          className={`audio-recorder__playButton ${
                            isPlaying
                              ? "audio-recorder__playButton--active"
                              : ""
                          }`}
                          onClick={() => handleTogglePlayback(recording.id)}
                          aria-label={
                            isPlaying ? "Pause recording" : "Play recording"
                          }
                        >
                          {isPlaying ? (
                            <span className="audio-recorder__pauseIcon">
                              <i />
                              <i />
                            </span>
                          ) : (
                            <span className="audio-recorder__playIcon" />
                          )}
                        </button>

                        <div className="audio-recorder__seekWrap">
                          <div className="audio-recorder__timeRow">
                            <span>{formatTime(playerState.currentTime)}</span>
                            <span>{formatTime(duration)}</span>
                          </div>

                          <div className="audio-recorder__seekShell">
                            <div
                              className="audio-recorder__seekFill"
                              style={{ width: `${progress}%` }}
                            />

                            <input
                              className="audio-recorder__seekInput"
                              type="range"
                              min="0"
                              max={duration || 0}
                              step="0.01"
                              value={playerState.currentTime}
                              onChange={(event) =>
                                handleSeekRecording(
                                  recording.id,
                                  event.target.value,
                                )
                              }
                              aria-label="Seek recording"
                            />
                          </div>
                        </div>

                        <audio
                          ref={(audio) => {
                            audioRefs.current[recording.id] = audio;
                          }}
                          src={recording.objectUrl}
                          preload="metadata"
                          onLoadedMetadata={() =>
                            handleLoadedMetadata(recording.id)
                          }
                          onTimeUpdate={() => handleTimeUpdate(recording.id)}
                          onEnded={() => handlePlayerEnded(recording.id)}
                        />
                      </div>

                      <div className="audio-recorder__itemActions audio-recorder__itemActions--modern">
                        <a
                          className="audio-recorder__download"
                          href={recording.objectUrl}
                          download={recording.name}
                        >
                          Download
                        </a>

                        {recording.uploadStatus === "failed" && (
                          <button
                            type="button"
                            className="audio-recorder__workflowReupload audio-recorder__itemReupload"
                            onClick={() =>
                              uploadRecordingWithPresignedUrls(recording)
                            }
                            disabled={
                              isRecording ||
                              recording.uploadStatus === "uploading" ||
                              !canUploadAndTranscribe
                            }
                            title={
                              !canUploadAndTranscribe
                                ? uploadDisabledMessage
                                : "Re-upload"
                            }
                          >
                            Re-upload
                          </button>
                        )}

                        <button
                          type="button"
                          className="audio-recorder__delete"
                          onClick={() => askToDeleteRecording(recording.id)}
                          disabled={
                            isRecording ||
                            recording.uploadStatus === "uploading"
                          }
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div
            className={`audio-recorder__bottomPreview ${
              isPreviewOpen ? "audio-recorder__bottomPreview--open" : ""
            } ${previewRecording ? "audio-recorder__bottomPreview--hasAudio" : ""} ${
              isPreviewDraggingFile
                ? "audio-recorder__bottomPreview--dragging"
                : ""
            }`}
          >
            <button
              type="button"
              className="audio-recorder__previewSummary"
              onClick={() => setIsPreviewOpen((prev) => !prev)}
              aria-expanded={isPreviewOpen}
            >
              <span className="audio-recorder__previewIcon" aria-hidden="true">
                {previewRecording ? "Audio" : "+"}
              </span>

              <span className="audio-recorder__previewText">
                <strong>
                  {previewRecording ? "Debug preview" : "Debug audio upload"}
                </strong>
                <small>
                  {previewRecording
                    ? `${previewRecording.name} - ${previewRecording.durationText}`
                    : "Drop or click to upload audio and run the same process."}
                </small>
              </span>

              <span className="audio-recorder__previewStatus">
                {previewStatusLabel}
              </span>
              <span
                className="audio-recorder__previewChevron"
                aria-hidden="true"
              />
            </button>

            <div
              className="audio-recorder__previewBody"
              aria-hidden={!isPreviewOpen}
            >
              <div
                className={`audio-recorder__previewDropBox ${
                  isPreviewDraggingFile
                    ? "audio-recorder__previewDropBox--dragging"
                    : ""
                }`}
                onDragOver={(event) => {
                  event.preventDefault();
                  setIsPreviewDraggingFile(true);
                }}
                onDragLeave={() => setIsPreviewDraggingFile(false)}
                onDrop={(event) => {
                  event.preventDefault();
                  void handleDebugPreviewFiles(event.dataTransfer.files);
                }}
                onClick={() => fileInputRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    fileInputRef.current?.click();
                  }
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="audio/*,.aac,.aif,.aiff,.flac,.m4a,.mp3,.mp4,.ogg,.opus,.wav,.webm"
                  hidden
                  onChange={(event) => {
                    if (event.target.files)
                      void handleDebugPreviewFiles(event.target.files);
                    event.currentTarget.value = "";
                  }}
                />
                <span
                  className="audio-recorder__previewDropIcon"
                  aria-hidden="true"
                >
                  Upload
                </span>
                <span className="audio-recorder__previewDropText">
                  Drop audio here or click to upload
                </span>
                <span className="audio-recorder__previewDropMeta">
                  It saves locally, uploads, transcribes, and generates the
                  patient note.
                </span>
              </div>

              {previewRecording && (
                <>
                  <div className="audio-recorder__previewPlayer">
                    <button
                      type="button"
                      className={`audio-recorder__previewPlayButton ${
                        isPreviewPlaying
                          ? "audio-recorder__previewPlayButton--active"
                          : ""
                      }`}
                      onClick={() => handleTogglePlayback(previewRecording.id)}
                      aria-label={
                        isPreviewPlaying ? "Pause preview" : "Play preview"
                      }
                    >
                      {isPreviewPlaying ? (
                        <span className="audio-recorder__pauseIcon">
                          <i />
                          <i />
                        </span>
                      ) : (
                        <span className="audio-recorder__playIcon" />
                      )}
                    </button>

                    <div className="audio-recorder__previewSeekWrap">
                      <div className="audio-recorder__timeRow audio-recorder__previewTimeRow">
                        <span>
                          {formatTime(previewPlayerState?.currentTime || 0)}
                        </span>
                        <span>{formatTime(previewDuration)}</span>
                      </div>

                      <div className="audio-recorder__seekShell audio-recorder__previewSeekShell">
                        <div
                          className="audio-recorder__seekFill audio-recorder__previewSeekFill"
                          style={{ width: `${previewProgress}%` }}
                        />

                        <input
                          className="audio-recorder__seekInput"
                          type="range"
                          min="0"
                          max={previewDuration || 0}
                          step="0.01"
                          value={previewPlayerState?.currentTime || 0}
                          onChange={(event) =>
                            handleSeekRecording(
                              previewRecording.id,
                              event.target.value,
                            )
                          }
                          aria-label="Seek preview recording"
                        />
                      </div>
                    </div>

                    <audio
                      ref={(audio) => {
                        audioRefs.current[previewRecording.id] = audio;
                      }}
                      src={previewRecording.objectUrl}
                      preload="metadata"
                      onLoadedMetadata={() =>
                        handleLoadedMetadata(previewRecording.id)
                      }
                      onTimeUpdate={() => handleTimeUpdate(previewRecording.id)}
                      onEnded={() => handlePlayerEnded(previewRecording.id)}
                    />
                  </div>

                  <div className="audio-recorder__previewActions">
                    <a
                      className="audio-recorder__download audio-recorder__previewDownload"
                      href={previewRecording.objectUrl}
                      download={previewRecording.name}
                    >
                      Download
                    </a>

                    {previewRecording.uploadStatus !== "uploading" &&
                      previewRecording.uploadStatus !== "uploaded" && (
                        <button
                          type="button"
                          className="audio-recorder__workflowReupload audio-recorder__previewReupload audio-recorder__previewUpload"
                          onClick={() =>
                            uploadRecordingWithPresignedUrls(previewRecording)
                          }
                          disabled={
                            isRecording ||
                            isWorkflowInProgress ||
                            isUploadInProgress ||
                            !canUploadAndTranscribe
                          }
                          title={
                            !canUploadAndTranscribe
                              ? uploadDisabledMessage
                              : undefined
                          }
                        >
                          {previewRecording.uploadStatus === "failed"
                            ? "Re-upload"
                            : "Upload & transcribe"}
                        </button>
                      )}

                    <button
                      type="button"
                      className="audio-recorder__delete audio-recorder__previewDelete"
                      onClick={() => askToDeleteRecording(previewRecording.id)}
                      disabled={
                        isRecording ||
                        previewRecording.uploadStatus === "uploading"
                      }
                    >
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          <section
            className="audio-recorder__bottomPreview audio-recorder__bottomPreview--open"
            aria-labelledby="audio-recorder-debug-s3-title"
          >
            <div className="audio-recorder__previewSummary">
              <span className="audio-recorder__previewIcon" aria-hidden="true">
                S3
              </span>

              <span className="audio-recorder__previewText">
                <strong id="audio-recorder-debug-s3-title">
                  Dev debug: presigned URL + S3 upload only
                </strong>
                <small>
                  No upload-complete, transcription, or patient-note request is
                  made.
                </small>
              </span>

              <span className="audio-recorder__previewStatus">
                {debugS3Upload.status === "requesting"
                  ? "Getting URL"
                  : debugS3Upload.status === "uploading"
                    ? `Uploading ${debugS3Upload.progress}%`
                    : debugS3Upload.status === "partsUploaded"
                      ? "Parts uploaded"
                      : debugS3Upload.status === "failed"
                        ? "Failed"
                        : "Ready"}
              </span>
            </div>

            <div className="audio-recorder__previewBody">
              <div
                className={`audio-recorder__previewDropBox ${
                  isDebugS3DraggingFile
                    ? "audio-recorder__previewDropBox--dragging"
                    : ""
                }`}
                onDragOver={(event) => {
                  event.preventDefault();
                  setIsDebugS3DraggingFile(true);
                }}
                onDragLeave={() => setIsDebugS3DraggingFile(false)}
                onDrop={(event) => {
                  event.preventDefault();
                  void handleDebugS3OnlyUpload(event.dataTransfer.files);
                }}
                onClick={() => debugS3FileInputRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    debugS3FileInputRef.current?.click();
                  }
                }}
                aria-disabled={
                  debugS3Upload.status === "requesting" ||
                  debugS3Upload.status === "uploading"
                }
              >
                <input
                  ref={debugS3FileInputRef}
                  type="file"
                  accept="audio/*,.aac,.aif,.aiff,.flac,.m4a,.mp3,.mp4,.ogg,.opus,.wav,.webm"
                  hidden
                  disabled={
                    debugS3Upload.status === "requesting" ||
                    debugS3Upload.status === "uploading"
                  }
                  onChange={(event) => {
                    if (event.target.files) {
                      void handleDebugS3OnlyUpload(event.target.files);
                    }
                    event.currentTarget.value = "";
                  }}
                />

                <span
                  className="audio-recorder__previewDropIcon"
                  aria-hidden="true"
                >
                  Upload
                </span>
                <span className="audio-recorder__previewDropText">
                  Drop audio here or click to upload only to S3
                </span>
                <span className="audio-recorder__previewDropMeta">
                  Calls generate_upload_url, then PUTs the audio parts directly
                  to S3.
                </span>
              </div>

              {debugS3Upload.status !== "idle" && (
                <div
                  className="audio-recorder__previewPlayer"
                  aria-live="polite"
                >
                  <div className="audio-recorder__previewSeekWrap">
                    <strong>{debugS3Upload.fileName}</strong>
                    <div className="audio-recorder__timeRow audio-recorder__previewTimeRow">
                      <span>
                        {debugS3Upload.status === "requesting"
                          ? "Requesting presigned URL..."
                          : debugS3Upload.status === "partsUploaded"
                            ? "All parts uploaded. Multipart upload was not finalized."
                            : debugS3Upload.status === "failed"
                              ? debugS3Upload.errorMessage
                              : `Uploaded ${debugS3Upload.uploadedParts} of ${debugS3Upload.totalParts} parts`}
                      </span>
                      <span>{debugS3Upload.progress}%</span>
                    </div>

                    <progress
                      value={debugS3Upload.progress}
                      max={100}
                      aria-label="Debug S3 upload progress"
                      style={{ width: "100%" }}
                    />

                    {debugS3Upload.uploadSessionId && (
                      <small>
                        Upload session: {debugS3Upload.uploadSessionId}
                      </small>
                    )}
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      {patientNoteEditor.isOpen && (
        <div
          className="audio-recorder__patientNoteOverlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="audio-recorder-patient-note-title"
        >
          <div className="audio-recorder__patientNoteModal">
            <div className="audio-recorder__patientNoteHeader">
              <div>
                <p className="audio-recorder__patientNoteEyebrow">
                  Patient note generated
                </p>
                <h3 id="audio-recorder-patient-note-title">
                  Review and edit patient note
                </h3>
                <span>
                  {patientNoteEditor.uploadSessionId
                    ? `Upload session: ${patientNoteEditor.uploadSessionId}`
                    : `Audio ID: ${patientNoteEditor.audioId || "-"}`}
                </span>
              </div>

              {/* <button
                type="button"
                className="audio-recorder__patientNoteClose"
                onClick={handleClosePatientNoteEditor}
                disabled={patientNoteEditor.isSaving}
                aria-label="Close patient note editor"
              >
                X
              </button> */}
            </div>

            <div
              key={`${patientNoteEditor.uploadSessionId}-${patientNoteEditor.status}`}
              ref={patientNoteEditorRef}
              className="audio-recorder__patientNoteRichEditor audio-recorder__patientNoteCkEditor"
              role="textbox"
              aria-label="Editable patient note"
              aria-multiline="true"
              aria-disabled={patientNoteEditor.isSaving}
              aria-describedby="audio-recorder-patient-note-review-status"
              data-placeholder="Patient note is empty. Type the note here before saving."
              onScroll={handlePatientNoteEditorScroll}
              onWheel={handlePatientNoteEditorUserInteraction}
              onMouseDown={handlePatientNoteEditorUserInteraction}
              onTouchMove={handlePatientNoteEditorUserInteraction}
              onKeyDown={handlePatientNoteEditorUserInteraction}
            >
              <textarea
                ref={patientNoteTextareaRef}
                defaultValue={
                  patientNoteEditor.patientNoteHtml ||
                  formatPatientNoteHtmlForEditor(
                    patientNoteEditor.patientNoteText,
                  )
                }
                aria-label="Editable patient note"
                disabled={patientNoteEditor.isSaving}
              />
            </div>

            <p
              id="audio-recorder-patient-note-review-status"
              className={`audio-recorder__patientNoteReviewHint ${
                isPatientNoteReviewedToBottom
                  ? "audio-recorder__patientNoteReviewHint--done"
                  : ""
              }`}
            >
              {patientNoteReviewMessage}
            </p>

            {patientNoteEditor.errorMessage && (
              <p className="audio-recorder__patientNoteError">
                {patientNoteEditor.errorMessage}
              </p>
            )}

            <div className="audio-recorder__patientNoteActions">
              <button
                type="button"
                className={`audio-recorder__patientNoteCopy ${
                  isPatientNoteCopied
                    ? "audio-recorder__patientNoteCopy--copied"
                    : ""
                }`}
                onClick={handleCopyPatientNote}
                disabled={isPatientNoteActionDisabled}
                aria-disabled={isPatientNoteActionDisabled}
                aria-label="Copy patient note to clipboard"
                title={
                  isPatientNoteReviewedToBottom
                    ? "Copy patient note to clipboard"
                    : "Scroll to the bottom of the patient note before copying"
                }
              >
                {isPatientNoteCopied ? "Copied" : "Copy to clipboard"}
              </button>

              {/* <button
                type="button"
                className="audio-recorder__patientNoteSecondary"
                onClick={handleClosePatientNoteEditor}
                disabled={patientNoteEditor.isSaving}
              >
                Close
              </button> */}

              <button
                type="button"
                className="audio-recorder__patientNotePrimary"
                onClick={handleSavePatientNote}
                disabled={isPatientNoteActionDisabled}
                aria-disabled={isPatientNoteActionDisabled}
                title={
                  isPatientNoteReviewedToBottom
                    ? "Confirm and save patient note"
                    : "Scroll to the bottom of the patient note before saving"
                }
              >
                {patientNoteEditor.isSaving ? "Saving..." : "Confirm and save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmModal && (
        <div
          className="audio-recorder__modalOverlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="audio-recorder-confirm-title"
        >
          <div
            className={`audio-recorder__confirmModal ${
              confirmModal.variant === "process"
                ? "audio-recorder__confirmModal--process"
                : ""
            }`}
          >
            <div
              className={`audio-recorder__confirmIcon ${
                confirmModal.danger ? "audio-recorder__confirmIcon--danger" : ""
              } ${
                confirmModal.variant === "process"
                  ? "audio-recorder__confirmIcon--process"
                  : ""
              }`}
            >
              {confirmModal.danger
                ? "!"
                : confirmModal.variant === "process"
                  ? "Go"
                  : "?"}
            </div>

            <div className="audio-recorder__confirmContent">
              <h3 id="audio-recorder-confirm-title">{confirmModal.title}</h3>
              <p>{confirmModal.message}</p>

              {confirmModal.details && confirmModal.details.length > 0 && (
                <ul className="audio-recorder__confirmDetails">
                  {confirmModal.details.map((detail) => (
                    <li key={detail}>{detail}</li>
                  ))}
                </ul>
              )}
            </div>

            <div className="audio-recorder__confirmActions">
              <button
                type="button"
                className="audio-recorder__confirmCancel"
                onClick={handleCancelConfirmAction}
              >
                {confirmModal.cancelLabel || "No"}
              </button>

              {confirmModal.action === "stop" && isRecording && !isPaused && (
                <button
                  type="button"
                  className="audio-recorder__confirmPause"
                  onClick={handlePauseFromConfirmModal}
                >
                  Pause for now
                </button>
              )}

              {/* {confirmModal.action === "startTranscribe" && (
                <button
                  type="button"
                  className="audio-recorder__confirmRetake audio-recorder__retakeIconButton"
                  onClick={handleRetakeFromConfirmModal}
                  title="Retake"
                  aria-label="Retake"
                >
                  <RetakeIcon />
                </button>
              )} */}

              <button
                type="button"
                className={`audio-recorder__confirmSubmit ${
                  confirmModal.danger
                    ? "audio-recorder__confirmSubmit--danger"
                    : ""
                } ${
                  confirmModal.variant === "process"
                    ? "audio-recorder__confirmSubmit--process"
                    : ""
                }`}
                onClick={handleConfirmAction}
              >
                {confirmModal.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AudioRecorder;
